<template>
  <div class="node-wrapper-v2" v-loading.fullscreen.lock="uploading">
    <div v-if="!game" style="text-align: center; padding: 40px; color: #909399;">请选择游戏</div>
    <template v-else>
    <!-- 游戏概要编辑区 -->
    <el-card class="summary-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">📖 游戏概要</span>
      </div>
      <el-row :gutter="24" class="summary-row">
        <!-- 左侧区域：标题、模型、故事概要 -->
        <el-col :span="16">
          <el-form label-width="80px" class="summary-form">
            <el-form-item label="标题">
              <el-input v-model="game.title" placeholder="请输入游戏标题" />
            </el-form-item>

            <el-form-item label="推理模型">
              <el-select v-model="game.model_id" placeholder="请选择模型" filterable clearable style="width: 100%;">
                <el-option
                  v-for="m in models"
                  :key="m.id"
                  :label="`${m.model}(${m.channel})`"
                  :value="m.id"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="故事概要" class="summary-item">
              <el-input
                type="textarea"
                v-model="game.description"
                :rows="6"
                placeholder="请输入故事概要..."
                class="summary-textarea"
              />
            </el-form-item>

            <el-form-item label="小说标签" :required="true">
              <div>
                <el-tag
                  v-for="tag in gameTags"
                  :key="tag"
                  closable
                  @close="handleTagClose(tag)"
                  style="margin-right: 8px; margin-bottom: 4px;"
                >
                  {{ tag }}
                </el-tag>
                <el-input
                  v-if="tagInputVisible"
                  ref="tagInput"
                  v-model="tagInputValue"
                  size="small"
                  style="width: 120px;"
                  @keyup.enter.native="handleTagConfirm"
                  @blur="handleTagConfirm"
                />
                <el-button v-else size="small" @click="showTagInput">+ 添加标签</el-button>
              </div>
            </el-form-item>

            <el-form-item label="小说分类">
              <div>
                <el-tag
                  v-for="cat in gameCategory"
                  :key="cat"
                  closable
                  type="success"
                  @close="handleCategoryClose(cat)"
                  style="margin-right: 8px; margin-bottom: 4px;"
                >
                  {{ cat }}
                </el-tag>
                <el-input
                  v-if="categoryInputVisible"
                  ref="categoryInput"
                  v-model="categoryInputValue"
                  size="small"
                  style="width: 120px;"
                  @keyup.enter.native="handleCategoryConfirm"
                  @blur="handleCategoryConfirm"
                />
                <el-button v-else size="small" @click="showCategoryInput">+ 添加分类</el-button>
              </div>
            </el-form-item>

            <el-form-item label="连载状态">
              <el-radio-group v-model="gameSerialStatus">
                <el-radio :label="0">连载中</el-radio>
                <el-radio :label="1">完结</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="热度加成">
              <el-input-number v-model="gameHeatBoost" :min="0" :step="10000" />
              <span style="margin-left: 8px; color: #909399; font-size: 12px;">当前热度 = 基础(10000) + 真实数据 + 加成({{ gameHeatBoost }})</span>
            </el-form-item>
          </el-form>
        </el-col>

        <!-- 右侧区域：封面 -->
        <el-col :span="8" class="cover-col">
          <div class="cover-section">
            <div class="cover-label">封面</div>
            <div class="cover-upload-wrapper">
              <el-upload
                class="cover-uploader"
                :action="uploadUrl"
                :headers="uploadHeaders"
                :data="{ game_id: game.id }"
                :on-success="handleCoverUploadSuccess"
                :show-file-list="false"
                :before-upload="() => { uploading = true; return true; }"
              >
                <div class="cover-preview">
                  <img v-if="game.url" :src="game.url" class="cover-image" />
                  <div v-else class="cover-placeholder">
                    <i class="el-icon-plus"></i>
                    <div class="placeholder-text">点击上传封面</div>
                  </div>
                  <div class="cover-overlay">
                    <i class="el-icon-edit"></i>
                    <div class="overlay-text">点击更换封面</div>
                  </div>
                </div>
              </el-upload>
            </div>
          </div>
        </el-col>
      </el-row>

      <div style="text-align: center; margin-top: 24px;">
        <el-button type="primary" @click="saveGame" icon="el-icon-check" size="medium">保存概要</el-button>
      </div>
    </el-card>

    <!-- 管理入口 -->
    <div class="management-bar">
      <el-button @click="showRoleDialog = true" icon="el-icon-user">角色管理</el-button>
      <el-button @click="showAttributeDialog = true" icon="el-icon-s-data">属性管理</el-button>
      <el-button @click="showShopDialog = true" icon="el-icon-goods">商城管理</el-button>
      <el-button @click="showCostumeDialog = true" icon="el-icon-shopping-bag-2">衣橱管理</el-button>
    </div>

    <div class="section-header">
      <h2 class="section-title">🎬 节点列表{{ game.nodeVersion ? ` (${game.nodeVersion})` : '' }}</h2>
    </div>

    <draggable v-model="nodes" @end="onDragEnd" handle=".handle">
      <div
        v-for="node in nodes"
        :key="node.id"
        class="node-block"
      >
        <div class="node-header">
          <div class="node-header-left">
            <div class="handle drag-handle">
              <i class="el-icon-rank"></i>
            </div>
            <span class="node-title-text">{{ node.title || '未命名节点' }}</span>
            <el-tag :type="node.node_type === 'ai' ? 'success' : 'info'" size="small" style="margin-left: 8px;">
              {{ node.node_type === 'ai' ? 'AI场景' : '固定剧情' }}
            </el-tag>
          </div>
          <div class="node-header-right">
            <el-button-group>
              <el-button size="mini" @click="toggleNodeExpanded(node)" icon="el-icon-arrow-down">
                {{ node.expanded ? '收起' : '展开' }}
              </el-button>
              <el-button size="mini" type="primary" @click="copyNode(node)" icon="el-icon-document-copy">
                复制
              </el-button>
            </el-button-group>
            <el-button-group style="margin-left: 8px;">
              <el-button size="mini" @click="moveNodeToPosition(node, 'top')" icon="el-icon-top">置顶</el-button>
              <el-button size="mini" @click="moveNodeToPosition(node, 'bottom')" icon="el-icon-bottom">置底</el-button>
              <el-button size="mini" @click="openMoveDialog(node)" icon="el-icon-sort">插入</el-button>
            </el-button-group>
          </div>
        </div>
        <div v-if="node.expanded">
          <!-- 固定节点使用新布局 -->
          <template v-if="node.node_type === 'fixed'">
            <div class="fixed-node-layout">
              <!-- 左侧内容区 -->
              <div class="fixed-node-left">
                <el-form label-width="120px" :model="node">
                  <!-- 第一行：节点类型, ID, 大章节号 -->
                  <el-row :gutter="20">
                    <el-col :span="8">
                      <el-form-item label="节点类型">
                        <el-select v-model="node.node_type" placeholder="请选择类型">
                          <el-option
                            v-for="opt in nodeTypeOptions"
                            :key="opt.value"
                            :label="opt.label"
                            :value="opt.value"
                          />
                        </el-select>
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="ID">
                        <el-input v-model="node.id" :disabled="true" />
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="大章节号">
                        <el-select v-model="node.game_chapter_id" placeholder="请选择大章节" filterable clearable>
                          <el-option
                            v-for="c in chapters"
                            :key="c.id"
                            :label="c.title"
                            :value="c.id"
                          />
                        </el-select>
                      </el-form-item>
                    </el-col>
                  </el-row>

                  <!-- 标题 -->
                  <el-form-item label="标题">
                    <el-input v-model="node.title" placeholder="请输入节点标题" />
                  </el-form-item>

                  <!-- 剧情输入区 -->
                  <el-form-item label="剧情输入">
                    <el-input
                      type="textarea"
                      v-model="node.dialogue_text"
                      :autosize="{ minRows: 6, maxRows: 20 }"
                      placeholder="请输入剧情对话，每行格式为：角色名:对话内容\n例如：\n旁白:在一片混沌的黑暗中，你听到有人在喊你的名字\n李明:你醒了吗？"
                      class="fixed-node-textarea"
                    />
                    <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                      提示：每行以"角色名:"开头，后面跟对话内容。保存时会自动解析成对话列表。
                    </div>
                  </el-form-item>
                </el-form>
              </div>

              <!-- 右侧媒体资源区 -->
              <div class="fixed-node-right">
                <!-- 背景图 -->
                <div class="media-section bg-section">
                  <el-upload
                    class="bg-upload-wrapper"
                    :action="uploadUrl"
                    :headers="uploadHeaders"
                    :data="{ game_id: game.id }"
                    :on-success="(res) => handleUploadSuccess(res, node, 'bg_url')"
                    :show-file-list="false"
                    :before-upload="() => { uploading = true; return true; }"
                  >
                    <div class="bg-preview-container">
                      <div class="bg-preview">
                        <img v-if="node.bg_url" :src="node.bg_url" class="bg-image" />
                        <div v-else class="bg-placeholder">
                          <i class="el-icon-picture"></i>
                          <div>背景图</div>
                        </div>
                        <div class="bg-overlay">
                          <i class="el-icon-edit"></i>
                          <div class="overlay-text">点击更换背景图</div>
                        </div>
                      </div>
                      <div class="bg-filename">
                        <span class="label">封面</span>
                        <span class="filename">{{ node.bg_display_name || '封面文件名' }}</span>
                      </div>
                    </div>
                  </el-upload>
                </div>

                <!-- 音乐播放器 -->
                <div class="media-section music-section">
                  <div class="music-player">
                    <audio
                      v-if="node.music_url"
                      ref="audioPlayerRef"
                      :data-id="node.id"
                      :src="node.music_url"
                      controls
                      style="width: 100%; height: 40px;"
                    />
                    <div v-else class="music-placeholder">
                      <i class="el-icon-headset"></i>
                      <span>音乐播放器</span>
                    </div>
                  </div>
                  <el-upload
                    class="music-upload-wrapper"
                    :action="uploadUrl"
                    :headers="uploadHeaders"
                    :data="{ game_id: game.id }"
                    :on-success="(res) => handleUploadSuccess(res, node, 'music_url')"
                    :show-file-list="false"
                    :before-upload="() => { uploading = true; return true; }"
                  >
                    <div class="music-filename">
                      <span class="label">音乐</span>
                      <span class="filename">{{ node.music_display_name || '音乐文件名' }}</span>
                      <div class="music-overlay">
                        <i class="el-icon-edit"></i>
                        <span>点击更换背景音乐</span>
                      </div>
                    </div>
                  </el-upload>
                </div>
              </div>
            </div>

            <!-- 固定节点的保存和删除按钮 -->
            <div class="fixed-node-actions">
              <el-button type="primary" @click="updateNodeData(node)" icon="el-icon-check">保存</el-button>
              <el-button type="danger" @click="deleteNodeData(node.id)" icon="el-icon-delete">删除</el-button>
              <el-button type="warning" plain @click="doExpand(node)" :icon="expandStates[node.id] && expandStates[node.id].loading ? 'el-icon-loading' : 'el-icon-s-opportunity'">{{ expandStates[node.id] && expandStates[node.id].loading ? '生成中' : (expandStates[node.id] && expandStates[node.id].text ? '查看扩写' : 'AI扩写') }}</el-button>
            </div>
          </template>

          <!-- AI节点使用新布局 -->
          <template v-else>
            <div class="ai-node-layout">
              <!-- 左侧内容区 -->
              <div class="ai-node-left">
                <el-form label-width="120px" :model="node">
                  <!-- 第一行：节点类型, ID, 大章节号 -->
                  <el-row :gutter="20">
                    <el-col :span="8">
                      <el-form-item label="节点类型">
                        <el-select v-model="node.node_type" placeholder="请选择类型">
                          <el-option
                            v-for="opt in nodeTypeOptions"
                            :key="opt.value"
                            :label="opt.label"
                            :value="opt.value"
                          />
                        </el-select>
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="ID">
                        <el-input v-model="node.id" :disabled="true" />
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="大章节号">
                        <el-select v-model="node.game_chapter_id" placeholder="请选择大章节" filterable clearable>
                          <el-option
                            v-for="c in chapters"
                            :key="c.id"
                            :label="c.title"
                            :value="c.id"
                          />
                        </el-select>
                      </el-form-item>
                    </el-col>
                  </el-row>

                  <!-- 标题 -->
                  <el-form-item label="标题">
                    <el-input v-model="node.title" placeholder="请输入节点标题" />
                  </el-form-item>

                  <!-- 故事背景 -->
                  <el-form-item label="故事背景">
                    <div style="display: flex; align-items: flex-start; gap: 8px;">
                      <el-input type="textarea" v-model="node.story_background" :autosize="{ minRows: 3, maxRows: 6 }" placeholder="请输入故事背景..." style="flex: 1;" />
                      <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'background', '故事背景')">调整</el-button>
                    </div>
                  </el-form-item>

                  <!-- 当前剧情设置 -->
                  <el-form-item label="当前剧情" class="current-plot-item">
                    <div class="current-plot-container">
                      <div style="display: flex; justify-content: flex-end;">
                        <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'current_plot', '当前剧情')">调整当前剧情</el-button>
                      </div>
                      <div class="plot-row">
                        <label class="plot-label">剧情开端</label>
                        <el-input
                          type="textarea"
                          v-model="node.current_plot_new.plot_start"
                          :rows="3"
                          placeholder="请输入开始时的剧情描述..."
                          class="plot-textarea"
                        />
                      </div>
                      <div class="plot-row">
                        <label class="plot-label">剧情过程</label>
                        <el-input
                          type="textarea"
                          v-model="node.current_plot_new.plot_process"
                          :rows="5"
                          placeholder="请输入进行中的剧情描述..."
                          class="plot-textarea"
                        />
                      </div>
                      <!-- 多结局管理 -->
                      <div class="plot-row" style="flex-direction: column; align-items: stretch;">
                        <label class="plot-label" style="width: auto; margin-bottom: 8px;">剧情结尾（多结局）</label>
                        <div v-for="(ending, eIdx) in node.current_plot_new.plot_end" :key="'ending-' + eIdx" style="border: 1px solid #E6A23C; padding: 12px; margin-bottom: 10px; border-radius: 4px; background: #fdf6ec;">
                          <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <el-input v-model="ending.name" placeholder="结局名称" style="width: 150px; margin-right: 10px;" size="small" />
                            <el-select v-model="ending.next_node_id" placeholder="跳转节点（可选）" clearable filterable style="width: 200px; margin-right: 10px;" size="small">
                              <el-option v-for="n in nodes.filter(nd => nd.id !== node.id)" :key="n.id" :label="`#${n.id} ${n.title}`" :value="n.id" />
                            </el-select>
                            <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEnding(node, eIdx)">删除</el-button>
                          </div>
                          <el-input type="textarea" v-model="ending.content" :rows="3" placeholder="请输入结局故事内容..." style="margin-bottom: 8px;" />
                          <!-- 结局输出示例 -->
                          <div style="margin-top: 8px;">
                            <span style="font-size: 12px; color: #909399;">输出示例：</span>
                            <div v-for="(ex, exIdx) in ending.output_examples" :key="'eoe-' + exIdx" style="display: flex; align-items: center; margin-top: 4px;">
                              <el-input type="textarea" v-model="ex.content" :rows="2" placeholder="输出示例内容" style="flex: 1; margin-right: 8px;" />
                              <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'ending_example', '结尾示例', null, findIndicatorIndexByEnding(node, ending))" style="margin-right: 4px;">调整</el-button>
                              <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEndingOutputExample(ending, exIdx)" />
                            </div>
                            <el-button type="primary" plain size="mini" @click="addEndingOutputExample(ending)" style="margin-top: 4px;">添加输出示例</el-button>
                          </div>
                        </div>
                        <el-button type="warning" plain size="mini" @click="addEnding(node)">添加结局</el-button>
                      </div>
                    </div>
                  </el-form-item>

                  <!-- 图片提示词 -->
                  <el-form-item label="图片提示词">
                    <div style="display: flex; align-items: flex-start; gap: 8px;">
                      <el-input type="textarea" v-model="node.image_prompt" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入图片提示词..." style="flex: 1;" />
                      <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'image_prompt', '图片提示词')">调整</el-button>
                    </div>
                  </el-form-item>
                </el-form>
              </div>

              <!-- 右侧媒体资源区 -->
              <div class="ai-node-right">
                <!-- 背景图 -->
                <div class="media-section bg-section">
                  <el-upload
                    class="bg-upload-wrapper"
                    :action="uploadUrl"
                    :headers="uploadHeaders"
                    :data="{ game_id: game.id }"
                    :on-success="(res) => handleUploadSuccess(res, node, 'bg_url')"
                    :show-file-list="false"
                    :before-upload="() => { uploading = true; return true; }"
                  >
                    <div class="bg-preview-container">
                      <div class="bg-preview">
                        <img v-if="node.bg_url" :src="node.bg_url" class="bg-image" />
                        <div v-else class="bg-placeholder">
                          <i class="el-icon-picture"></i>
                          <div>背景图</div>
                        </div>
                        <div class="bg-overlay">
                          <i class="el-icon-edit"></i>
                          <div class="overlay-text">点击更换背景图</div>
                        </div>
                      </div>
                      <div class="bg-filename">
                        <span class="label">封面</span>
                        <span class="filename">{{ node.bg_display_name || '封面文件名' }}</span>
                      </div>
                    </div>
                  </el-upload>
                </div>

                <!-- 音乐播放器 -->
                <div class="media-section music-section">
                  <div class="music-player">
                    <audio
                      v-if="node.music_url"
                      ref="audioPlayerRef"
                      :data-id="node.id"
                      :src="node.music_url"
                      controls
                      style="width: 100%; height: 40px;"
                    />
                    <div v-else class="music-placeholder">
                      <i class="el-icon-headset"></i>
                      <span>音乐播放器</span>
                    </div>
                  </div>
                  <el-upload
                    class="music-upload-wrapper"
                    :action="uploadUrl"
                    :headers="uploadHeaders"
                    :data="{ game_id: game.id }"
                    :on-success="(res) => handleUploadSuccess(res, node, 'music_url')"
                    :show-file-list="false"
                    :before-upload="() => { uploading = true; return true; }"
                  >
                    <div class="music-filename">
                      <span class="label">音乐</span>
                      <span class="filename">{{ node.music_display_name || '音乐文件名' }}</span>
                      <div class="music-overlay">
                        <i class="el-icon-edit"></i>
                        <span>点击更换背景音乐</span>
                      </div>
                    </div>
                  </el-upload>
                </div>
              </div>
            </div>

            <!-- AI节点设置区域 -->
            <div class="ai-node-settings">
              <el-form label-width="120px" :model="node">
                <el-divider content-position="center">
                  <span style="color: #6366f1; font-weight: 600; font-size: 15px;">AI节点设置</span>
                </el-divider>

                <!-- 进入条件 -->
                <el-form-item label="进入条件">
              <div v-for="(cond, cIdx) in node.entry_conditions" :key="cIdx" style="border: 1px solid #E6A23C; padding: 15px; margin-bottom: 10px; border-radius: 4px; background: #fdf6ec;">
                <el-row :gutter="10">
                  <el-col :span="6">
                    <el-select v-model="cond.type" placeholder="条件类型" style="width: 100%;">
                      <el-option label="货币" value="货币" />
                      <el-option label="属性" value="属性" />
                      <el-option label="好感度" value="好感度" />
                      <el-option label="道具" value="道具" />
                      <el-option label="服装" value="服装" />
                    </el-select>
                  </el-col>
                  <!-- 货币 -->
                  <template v-if="cond.type === '货币'">
                    <el-col :span="6">
                      <el-input-number v-model="cond.value" placeholder="数值" style="width: 100%;" />
                    </el-col>
                    <el-col :span="6">
                      <el-checkbox v-model="cond.is_free">是否限免</el-checkbox>
                    </el-col>
                  </template>
                  <!-- 属性 -->
                  <template v-else-if="cond.type === '属性'">
                    <el-col :span="6">
                      <el-select v-model="cond.attribute_name" placeholder="属性名称" style="width: 100%;">
                        <el-option v-for="attr in attributeNameList" :key="attr" :label="attr" :value="attr" />
                      </el-select>
                    </el-col>
                    <el-col :span="6">
                      <el-input-number v-model="cond.attribute_value" placeholder="数值要求" style="width: 100%;" />
                    </el-col>
                  </template>
                  <!-- 好感度 -->
                  <template v-else-if="cond.type === '好感度'">
                    <el-col :span="6">
                      <el-select v-model="cond.role_id" placeholder="角色" style="width: 100%;">
                        <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                      </el-select>
                    </el-col>
                    <el-col :span="6">
                      <el-input-number v-model="cond.affection_value" placeholder="数值要求" style="width: 100%;" />
                    </el-col>
                  </template>
                  <!-- 道具 -->
                  <template v-else-if="cond.type === '道具'">
                    <el-col :span="12">
                      <el-input v-model="cond.item_name" placeholder="道具名称" style="width: 100%;" />
                    </el-col>
                  </template>
                  <!-- 服装 -->
                  <template v-else-if="cond.type === '服装'">
                    <el-col :span="6">
                      <el-select v-model="cond.costume_role_id" placeholder="角色" style="width: 100%;" @change="onCostumeRoleChange(cond)">
                        <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                      </el-select>
                    </el-col>
                    <el-col :span="6">
                      <el-select v-model="cond.costume_name" placeholder="服装名称" style="width: 100%;" multiple>
                        <el-option v-for="costume in getCostumesForRole(cond.costume_role_id)" :key="costume.id" :label="costume.name" :value="costume.name" />
                      </el-select>
                    </el-col>
                  </template>
                  <el-col :span="2">
                    <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEntryCondition(node, cIdx)" />
                  </el-col>
                </el-row>
              </div>
                  <el-button type="warning" plain size="mini" @click="addEntryCondition(node)">添加进入条件</el-button>
                </el-form-item>

                <!-- 是否提示隐藏UI -->
                <el-form-item>
                  <el-checkbox v-model="node.hidden_ui">是否提示隐藏UI</el-checkbox>
                </el-form-item>

                <!-- 第二行：场景角色、开场白角色、回合数限制 -->
                <el-row :gutter="20">
                  <el-col :span="8">
                    <el-form-item label="场景角色">
                      <el-select v-model="node.role_ids" multiple placeholder="选择角色" style="width: 100%;">
                        <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="8">
                    <el-form-item label="开场白角色">
                      <el-select v-model="node.prologue_role_id" placeholder="选择角色" style="width: 100%;">
                        <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="8">
                    <el-form-item label="回合数限制">
                      <el-input-number v-model="node.limit_turn" :min="0" placeholder="输入回合数限制" style="width: 100%;" />
                    </el-form-item>
                  </el-col>
                </el-row>

                <!-- 开场白内容和攻略方式 -->
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item label="开场白内容">
                      <div style="display: flex; align-items: flex-start; gap: 8px;">
                        <el-input type="textarea" v-model="node.prologue" placeholder="输入开场白内容" :autosize="{ minRows: 3, maxRows: 6 }" style="flex: 1;" />
                        <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'prologue', '开场白')">调整</el-button>
                      </div>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="攻略方式">
                      <el-input type="textarea" v-model="node.node_task" :autosize="{ minRows: 3, maxRows: 6 }" placeholder="请输入攻略方式..." />
                    </el-form-item>
                  </el-col>
                </el-row>

              <!-- 进度指标管理 (v2.0) -->
              <el-divider>进度指标管理</el-divider>
              <el-form-item label="进度指标列表">
                <div v-for="(proc, pIdx) in node.process" :key="pIdx" style="border: 1px solid #409EFF; padding: 15px; margin-bottom: 15px; border-radius: 4px; background: #f0f9ff;">
                  <!-- 紧凑模式 -->
                  <div v-if="!proc._editing" style="display: flex; align-items: center; justify-content: space-between;">
                    <span style="flex: 1; color: #409EFF; font-size: 13px;">进度指标{{pIdx + 1}}: {{ formatProcessSummary(proc) }}</span>
                    <div>
                      <el-button type="primary" size="mini" @click="$set(proc, '_editing', true)">编辑</el-button>
                      <el-button type="danger" size="mini" @click="removeProcess(node, pIdx)">删除</el-button>
                    </div>
                  </div>
                  <!-- 展开模式 -->
                  <template v-else>
                    <div style="display: flex; align-items: center; margin-bottom: 10px;">
                      <span style="font-weight: bold; margin-right: 10px; color: #409EFF;">进度指标 {{pIdx + 1}}</span>
                      <el-button size="mini" @click="$set(proc, '_editing', false)">收起</el-button>
                      <el-button type="danger" size="mini" @click="removeProcess(node, pIdx)">删除</el-button>
                    </div>

                    <el-row :gutter="20">
                      <el-col :span="6">
                        <el-form-item label="指标名称">
                          <el-input v-model="proc.name" placeholder="如：愤怒值" />
                        </el-form-item>
                      </el-col>
                      <el-col :span="6">
                        <el-form-item label="起始值">
                          <el-input-number v-model="proc.start_process" :min="0" style="width: 100%;" />
                        </el-form-item>
                      </el-col>
                      <el-col :span="6">
                        <el-form-item label="目标值">
                          <el-input-number v-model="proc.end_process" :min="0" style="width: 100%;" />
                        </el-form-item>
                      </el-col>
                      <el-col :span="6">
                        <el-form-item label="绑定结局">
                          <el-select v-model="proc.ending_name" placeholder="选择结局（可选）" clearable style="width: 100%;">
                            <el-option v-for="name in getEndingList(node)" :key="name" :label="name" :value="name" />
                          </el-select>
                        </el-form-item>
                      </el-col>
                    </el-row>

                    <!-- 成就/道具/事件/好感度/属性奖励 -->
                    <el-form-item label="成就/道具/事件/好感度/属性">
                      <div v-for="(award, aIdx) in proc.award_conf" :key="aIdx" style="border: 1px solid #ccc; padding: 10px; margin-bottom: 8px; border-radius: 4px; background: white;">
                        <el-row :gutter="10">
                          <el-col :span="6">
                            <el-select v-model="award.type" placeholder="奖励类型" style="width: 100%;">
                              <el-option label="成就" value="成就" />
                              <el-option label="道具" value="道具" />
                              <el-option label="事件" value="事件" />
                              <el-option label="好感度" value="好感度" />
                              <el-option label="属性" value="属性" />
                            </el-select>
                          </el-col>
                          <template v-if="award.type === '成就' || award.type === '道具' || award.type === '事件'">
                            <el-col :span="16">
                              <el-input v-model="award.reward_description" placeholder="奖励描述" />
                            </el-col>
                          </template>
                          <template v-else-if="award.type === '好感度'">
                            <el-col :span="8">
                              <el-select v-model="award.role_id" placeholder="角色" style="width: 100%;">
                                <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                              </el-select>
                            </el-col>
                            <el-col :span="8">
                              <el-input-number v-model="award.affection_value" placeholder="好感度值" style="width: 100%;" />
                            </el-col>
                          </template>
                          <template v-else-if="award.type === '属性'">
                            <el-col :span="8">
                              <el-select v-model="award.attribute_name" placeholder="属性名称" style="width: 100%;">
                                <el-option v-for="attr in attributeNameList" :key="attr" :label="attr" :value="attr" />
                              </el-select>
                            </el-col>
                            <el-col :span="8">
                              <el-input-number v-model="award.attribute_value" placeholder="属性值" style="width: 100%;" />
                            </el-col>
                          </template>
                          <el-col :span="2">
                            <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeAwardConf(proc, aIdx)" />
                          </el-col>
                        </el-row>
                      </div>
                      <el-button type="primary" plain size="mini" @click="addAwardConf(proc)">添加奖励</el-button>
                    </el-form-item>
                  </template>
                </div>
                <el-button type="success" plain @click="addProcess(node)">添加新进度指标</el-button>
              </el-form-item>

              <!-- 用户消息处理 (v2.0) -->
              <el-divider>用户消息处理</el-divider>
              <el-form-item label="用户消息列表">
                <div v-if="node.user_messages_new && node.user_messages_new.length > 0" style="color: #409EFF; margin-bottom: 10px; font-size: 12px;">
                  当前共 {{node.user_messages_new.length}} 条消息
                </div>
                <div v-for="(msg, msgIdx) in node.user_messages_new" :key="'msg-' + msgIdx" style="border: 1px solid #66b1ff; padding: 15px; margin-bottom: 10px; border-radius: 4px; background: #ecf5ff;">
                  <div style="display: flex; align-items: center; margin-bottom: 10px;">
                    <span style="font-weight: bold; margin-right: 10px;">消息 {{msgIdx + 1}}</span>
                    <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeUserMessage(node, msgIdx)">删除</el-button>
                  </div>

                  <el-form-item label="如果用户发的消息">
                    <div style="display: flex; align-items: flex-start; gap: 8px;">
                      <el-input type="textarea" v-model="msg.result" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="例如：用户提到了某个关键词..." style="flex: 1;" />
                      <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'user_message_trigger', '触发条件', msgIdx)">调整</el-button>
                    </div>
                  </el-form-item>

                  <el-form-item label="则（AI回复方式）">
                    <div style="display: flex; align-items: flex-start; gap: 8px;">
                      <el-input type="textarea" v-model="msg.ai_reply" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="例如：AI应该如何回应..." style="flex: 1;" />
                      <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'user_message_ai_reply', 'AI回复', msgIdx)">调整</el-button>
                    </div>
                  </el-form-item>

                  <el-form-item label="进度指标名称">
                    <el-select v-model="msg.progress_name" placeholder="选择进度指标（可选）" clearable style="width: 100%;">
                      <el-option label="无" value="" />
                      <el-option
                        v-for="proc in getProcessList(node)"
                        :key="proc.name"
                        :label="proc.name"
                        :value="proc.name"
                      />
                    </el-select>
                  </el-form-item>

                  <el-form-item label="实化数值">
                    <el-input-number v-model="msg.progress_value" placeholder="进度增减值" style="width: 100%;" />
                  </el-form-item>

                  <!-- 用户消息输出示例 -->
                  <el-form-item label="输出示例">
                    <div v-for="(ex, exIdx) in msg.output_examples" :key="'umoe-' + exIdx" style="display: flex; align-items: center; margin-bottom: 4px;">
                      <el-input type="textarea" v-model="ex.content" :rows="2" placeholder="输出示例内容" style="flex: 1; margin-right: 8px;" />
                      <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'user_message_example', '输出示例', msgIdx)" style="margin-right: 4px;">调整</el-button>
                      <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeUserMsgOutputExample(msg, exIdx)" />
                    </div>
                    <el-button type="primary" plain size="mini" @click="addUserMsgOutputExample(msg)">添加输出示例</el-button>
                  </el-form-item>
                </div>
                <el-button type="primary" plain size="mini" @click="addUserMessage(node)" style="margin-top: 10px;">
                  添加用户消息处理
                </el-button>
              </el-form-item>

              <!-- 彩蛋管理 (v2.0) - 移到用户消息后面 -->
              <el-divider>彩蛋管理</el-divider>
              <el-form-item label="彩蛋列表">
                <div v-for="(egg, eIdx) in node.egg" :key="eIdx" style="border: 1px solid #67C23A; padding: 15px; margin-bottom: 15px; border-radius: 4px; background: #f0f9f0;">
                  <!-- 紧凑模式 -->
                  <div v-if="!egg._editing" style="display: flex; align-items: center; justify-content: space-between;">
                    <span style="flex: 1; color: #67C23A; font-size: 13px;">彩蛋{{eIdx + 1}}: {{ formatEggSummary(egg) }}</span>
                    <div>
                      <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'easter_egg_name', '彩蛋名称', null, null, eIdx)">调名称</el-button>
                      <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'easter_egg_trigger', '彩蛋触发条件', null, null, eIdx)">调触发</el-button>
                      <el-button type="primary" size="mini" @click="$set(egg, '_editing', true)">编辑</el-button>
                      <el-button type="danger" size="mini" @click="removeEgg(node, eIdx)">删除</el-button>
                    </div>
                  </div>
                  <!-- 展开模式 -->
                  <template v-else>
                    <div style="display: flex; align-items: center; margin-bottom: 10px;">
                      <span style="font-weight: bold; margin-right: 10px; color: #67C23A;">彩蛋 {{eIdx + 1}}</span>
                      <el-button size="mini" @click="$set(egg, '_editing', false)">收起</el-button>
                      <el-button type="danger" size="mini" @click="removeEgg(node, eIdx)">删除</el-button>
                    </div>

                    <el-form-item label="彩蛋名称">
                      <div style="display: flex; align-items: flex-start; gap: 8px;">
                        <el-input v-model="egg.egg_name" placeholder="请输入彩蛋名称" style="flex: 1;" />
                        <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'easter_egg_name', '彩蛋名称', null, null, eIdx)">调整</el-button>
                      </div>
                    </el-form-item>

                    <el-form-item label="触发条件">
                      <div style="display: flex; align-items: flex-start; gap: 8px;">
                        <el-input type="textarea" v-model="egg.trigger_condition" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入触发条件描述..." style="flex: 1;" />
                        <el-button size="mini" type="warning" plain icon="el-icon-magic-stick" @click="openRefineDialog(node, 'easter_egg_trigger', '彩蛋触发条件', null, null, eIdx)">调整</el-button>
                      </div>
                    </el-form-item>

                    <!-- 进度增益 -->
                    <el-form-item label="进度增益">
                      <div v-if="!node.process || !Array.isArray(node.process) || node.process.length === 0" style="color: #999; margin-bottom: 10px;">
                        请先添加进度指标，才能配置进度增益
                      </div>
                      <div v-for="(pg, pgIdx) in egg.process" :key="pgIdx" style="border: 1px solid #ccc; padding: 10px; margin-bottom: 8px; border-radius: 4px; background: white;">
                        <el-row :gutter="10">
                          <el-col :span="10">
                            <el-select v-model="pg.process_name" placeholder="选择进度指标" style="width: 100%;">
                              <el-option
                                v-for="proc in node.process"
                                :key="proc.name"
                                :label="proc.name"
                                :value="proc.name"
                              />
                            </el-select>
                          </el-col>
                          <el-col :span="12">
                            <el-input-number v-model="pg.process_gain" placeholder="增益值（可为负数）" style="width: 100%;" />
                          </el-col>
                          <el-col :span="2">
                            <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeProcessGain(egg, pgIdx)" />
                          </el-col>
                        </el-row>
                      </div>
                      <el-button type="primary" plain size="mini" @click="addProcessGain(egg, node)" :disabled="!node.process || !Array.isArray(node.process) || node.process.length === 0">添加进度增益</el-button>
                    </el-form-item>

                    <!-- 成就/道具/事件/好感度/属性奖励 -->
                    <el-form-item label="成就/道具/事件/好感度/属性">
                      <div v-for="(award, aIdx) in egg.award_conf" :key="aIdx" style="border: 1px solid #ccc; padding: 10px; margin-bottom: 8px; border-radius: 4px; background: white;">
                        <el-row :gutter="10">
                          <el-col :span="6">
                            <el-select v-model="award.type" placeholder="奖励类型" style="width: 100%;">
                              <el-option label="成就" value="成就" />
                              <el-option label="道具" value="道具" />
                              <el-option label="事件" value="事件" />
                              <el-option label="好感度" value="好感度" />
                              <el-option label="属性" value="属性" />
                            </el-select>
                          </el-col>
                          <template v-if="award.type === '成就' || award.type === '道具' || award.type === '事件'">
                            <el-col :span="16">
                              <el-input v-model="award.reward_description" placeholder="奖励描述" />
                            </el-col>
                          </template>
                          <template v-else-if="award.type === '好感度'">
                            <el-col :span="8">
                              <el-select v-model="award.role_id" placeholder="角色" style="width: 100%;">
                                <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                              </el-select>
                            </el-col>
                            <el-col :span="8">
                              <el-input-number v-model="award.affection_value" placeholder="好感度值" style="width: 100%;" />
                            </el-col>
                          </template>
                          <template v-else-if="award.type === '属性'">
                            <el-col :span="8">
                              <el-select v-model="award.attribute_name" placeholder="属性名称" style="width: 100%;">
                                <el-option v-for="attr in attributeNameList" :key="attr" :label="attr" :value="attr" />
                              </el-select>
                            </el-col>
                            <el-col :span="8">
                              <el-input-number v-model="award.attribute_value" placeholder="属性值" style="width: 100%;" />
                            </el-col>
                          </template>
                          <el-col :span="2">
                            <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEggAwardConf(egg, aIdx)" />
                          </el-col>
                        </el-row>
                      </div>
                      <el-button type="primary" plain size="mini" @click="addEggAwardConf(egg)">添加奖励</el-button>
                    </el-form-item>
                  </template>
                </div>
                <el-button type="success" plain @click="addEgg(node)">添加新彩蛋</el-button>
                </el-form-item>
              </el-form>
            </div>

            <!-- AI节点的保存和删除按钮 -->
            <div class="ai-node-actions">
              <el-button type="primary" @click="updateNodeData(node)" icon="el-icon-check">保存</el-button>
              <el-button type="danger" @click="deleteNodeData(node.id)" icon="el-icon-delete">删除</el-button>
              <el-button type="warning" plain @click="doExpand(node)" :icon="expandStates[node.id] && expandStates[node.id].loading ? 'el-icon-loading' : 'el-icon-s-opportunity'">{{ expandStates[node.id] && expandStates[node.id].loading ? '生成中' : (expandStates[node.id] && expandStates[node.id].text ? '查看扩写' : 'AI扩写') }}</el-button>
            </div>
          </template>
        </div>
        <el-divider />
      </div>
    </draggable>

    <h3>新增节点</h3>
    <el-form label-width="120px" :model="newNode">
      <!-- 第一行：节点类型, ID, 大章节号 -->
      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="节点类型">
            <el-select v-model="newNode.node_type" placeholder="请选择类型" @change="onNewNodeTypeChange">
              <el-option v-for="opt in nodeTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="ID">
            <el-input value="新建中..." :disabled="true" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="大章节号">
            <el-select v-model="newNode.game_chapter_id" placeholder="请选择大章节" filterable clearable>
              <el-option
                v-for="c in chapters"
                :key="c.id"
                :label="c.title"
                :value="c.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- 标题 -->
      <el-form-item label="标题">
        <el-input v-model="newNode.title" placeholder="请输入节点标题" />
      </el-form-item>

      <!-- 回合数限制（仅AI节点） -->
      <el-form-item v-if="newNode.node_type === 'ai'" label="回合数限制">
        <el-input-number v-model="newNode.limit_turn" :min="0" placeholder="输入回合数限制" style="width: 100%;" />
      </el-form-item>

      <!-- 描述（仅AI节点） -->
      <el-form-item v-if="newNode.node_type === 'ai'" label="描述">
        <el-input type="textarea" v-model="newNode.description" :autosize="{ minRows: 2, maxRows: 15 }"/>
      </el-form-item>

      <!-- 固定节点的剧情输入区 -->
      <el-form-item v-if="newNode.node_type === 'fixed'" label="剧情输入">
        <el-input 
          type="textarea" 
          v-model="newNode.dialogue_text" 
          :autosize="{ minRows: 6, maxRows: 20 }" 
          placeholder="请输入剧情对话，每行格式为：角色名:对话内容\n例如：\n旁白:在一片混沌的黑暗中，你听到有人在喊你的名字\n李明:你醒了吗？"
        />
        <div style="margin-top: 8px; color: #909399; font-size: 12px;">
          提示：每行以"角色名:"开头，后面跟对话内容。保存时会自动解析成对话列表。
        </div>
      </el-form-item>

      <!-- 进入条件（仅AI节点） -->
      <el-form-item v-if="newNode.node_type === 'ai'" label="进入条件">
        <div v-for="(cond, cIdx) in newNode.entry_conditions" :key="cIdx" style="border: 1px solid #E6A23C; padding: 15px; margin-bottom: 10px; border-radius: 4px; background: #fdf6ec;">
          <el-row :gutter="10">
            <el-col :span="6">
              <el-select v-model="cond.type" placeholder="条件类型" style="width: 100%;">
                <el-option label="货币" value="货币" />
                <el-option label="属性" value="属性" />
                <el-option label="好感度" value="好感度" />
                <el-option label="道具" value="道具" />
                <el-option label="服装" value="服装" />
              </el-select>
            </el-col>
            <!-- 货币 -->
            <template v-if="cond.type === '货币'">
              <el-col :span="6">
                <el-input-number v-model="cond.value" placeholder="数值" style="width: 100%;" />
              </el-col>
              <el-col :span="6">
                <el-checkbox v-model="cond.is_free">是否限免</el-checkbox>
              </el-col>
            </template>
            <!-- 属性 -->
            <template v-else-if="cond.type === '属性'">
              <el-col :span="6">
                <el-select v-model="cond.attribute_name" placeholder="属性名称" style="width: 100%;">
                  <el-option v-for="attr in attributeNameList" :key="attr" :label="attr" :value="attr" />
                </el-select>
              </el-col>
              <el-col :span="6">
                <el-input-number v-model="cond.attribute_value" placeholder="数值要求" style="width: 100%;" />
              </el-col>
            </template>
            <!-- 好感度 -->
            <template v-else-if="cond.type === '好感度'">
              <el-col :span="6">
                <el-select v-model="cond.role_id" placeholder="角色" style="width: 100%;">
                  <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                </el-select>
              </el-col>
              <el-col :span="6">
                <el-input-number v-model="cond.affection_value" placeholder="数值要求" style="width: 100%;" />
              </el-col>
            </template>
            <!-- 道具 -->
            <template v-else-if="cond.type === '道具'">
              <el-col :span="12">
                <el-input v-model="cond.item_name" placeholder="道具名称" style="width: 100%;" />
              </el-col>
            </template>
            <!-- 服装 -->
            <template v-else-if="cond.type === '服装'">
              <el-col :span="6">
                <el-select v-model="cond.costume_role_id" placeholder="角色" style="width: 100%;" @change="onCostumeRoleChange(cond)">
                  <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                </el-select>
              </el-col>
              <el-col :span="6">
                <el-select v-model="cond.costume_name" placeholder="服装名称" style="width: 100%;">
                  <el-option v-for="costume in getCostumesForRole(cond.costume_role_id)" :key="costume.id" :label="costume.name" :value="costume.name" />
                </el-select>
              </el-col>
            </template>
            <el-col :span="2">
              <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEntryCondition(newNode, cIdx)" />
            </el-col>
          </el-row>
        </div>
        <el-button type="warning" plain size="mini" @click="addEntryCondition(newNode)">添加进入条件</el-button>
      </el-form-item>

      <!-- 场景设置（仅AI节点） -->
      <template v-if="newNode.node_type === 'ai'">
        <!-- 场景中的角色 -->
        <el-form-item label="场景中的角色">
          <el-select v-model="newNode.role_ids" multiple placeholder="选择角色">
            <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
          </el-select>
        </el-form-item>

        <!-- 开场白：角色和内容 -->
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="开场白角色">
              <el-select v-model="newNode.prologue_role_id" placeholder="选择角色">
                <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="16">
            <el-form-item label="开场白内容">
              <el-input type="textarea" v-model="newNode.prologue" placeholder="输入开场白内容" />
            </el-form-item>
          </el-col>
        </el-row>
      </template>

      <!-- AI节点专有字段 -->
      <div v-if="newNode.node_type === 'ai'">
        <el-divider>AI节点设置</el-divider>

        <!-- 是否提示隐藏UI -->
        <el-form-item>
          <el-checkbox v-model="newNode.hidden_ui">是否提示隐藏UI</el-checkbox>
        </el-form-item>

        <!-- 故事背景 -->
        <el-form-item label="故事背景">
          <el-input type="textarea" v-model="newNode.story_background" :autosize="{ minRows: 3, maxRows: 6 }" placeholder="请输入故事背景..." />
        </el-form-item>

        <!-- 当前剧情 (新版 JSON 格式) -->
        <el-form-item label="当前剧情设置">
          <div style="border: 1px solid #ddd; padding: 15px; border-radius: 4px;">
            <el-form-item label="剧情开端">
              <el-input type="textarea" v-model="newNode.current_plot_new.plot_start" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入开始时的剧情描述..." />
            </el-form-item>
            <el-form-item label="剧情过程">
              <el-input type="textarea" v-model="newNode.current_plot_new.plot_process" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入进行中的剧情描述..." />
            </el-form-item>
            <!-- 多结局管理 -->
            <el-form-item label="剧情结尾（多结局）">
              <div v-for="(ending, eIdx) in newNode.current_plot_new.plot_end" :key="'new-ending-' + eIdx" style="border: 1px solid #E6A23C; padding: 12px; margin-bottom: 10px; border-radius: 4px; background: #fdf6ec;">
                <div style="display: flex; align-items: center; margin-bottom: 8px;">
                  <el-input v-model="ending.name" placeholder="结局名称" style="width: 150px; margin-right: 10px;" size="small" />
                  <el-select v-model="ending.next_node_id" placeholder="跳转节点（可选）" clearable filterable style="width: 200px; margin-right: 10px;" size="small">
                    <el-option v-for="n in nodes" :key="n.id" :label="`#${n.id} ${n.title}`" :value="n.id" />
                  </el-select>
                  <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEnding(newNode, eIdx)">删除</el-button>
                </div>
                <el-input type="textarea" v-model="ending.content" :rows="3" placeholder="请输入结局故事内容..." style="margin-bottom: 8px;" />
                <!-- 结局输出示例 -->
                <div style="margin-top: 8px;">
                  <span style="font-size: 12px; color: #909399;">输出示例：</span>
                  <div v-for="(ex, exIdx) in ending.output_examples" :key="'neoe-' + exIdx" style="display: flex; align-items: center; margin-top: 4px;">
                    <el-input type="textarea" v-model="ex.content" :rows="2" placeholder="输出示例内容" style="flex: 1; margin-right: 8px;" />
                    <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEndingOutputExample(ending, exIdx)" />
                  </div>
                  <el-button type="primary" plain size="mini" @click="addEndingOutputExample(ending)" style="margin-top: 4px;">添加输出示例</el-button>
                </div>
              </div>
              <el-button type="warning" plain size="mini" @click="addEnding(newNode)">添加结局</el-button>
            </el-form-item>
          </div>
        </el-form-item>

        <!-- 图片提示词 -->
        <el-form-item label="图片提示词">
          <el-input type="textarea" v-model="newNode.image_prompt" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入图片提示词..." />
        </el-form-item>

        <!-- 攻略方式 -->
        <el-form-item label="攻略方式">
          <el-input type="textarea" v-model="newNode.node_task" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入攻略方式..." />
        </el-form-item>

        <!-- 角色设置 -->
        <el-form-item label="角色设置">
          <div v-for="(roleConfig, index) in newNode.user_special" :key="index" style="border: 1px solid #ddd; padding: 15px; margin-bottom: 10px; border-radius: 4px;">
            <div style="display: flex; align-items: center; margin-bottom: 10px;">
              <span style="font-weight: bold; margin-right: 10px;">角色配置 {{index + 1}}</span>
              <el-button type="danger" size="mini" @click="removeRoleConfig(newNode, index)">删除</el-button>
            </div>
            <el-form-item label="角色名称">
              <el-select v-model="roleConfig.role_id" placeholder="选择角色">
                <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
              </el-select>
            </el-form-item>
            <el-form-item label="角色任务">
              <el-input type="textarea" v-model="roleConfig.task" placeholder="请输入角色任务" />
            </el-form-item>
            <el-form-item label="说话时机">
              <el-input type="textarea" v-model="roleConfig.speak_timing" placeholder="请输入说话时机" />
            </el-form-item>
            <el-form-item label="说话语气">
              <el-input type="textarea" v-model="roleConfig.speak_tone" placeholder="请输入说话语气" />
            </el-form-item>
            <el-form-item label="人设">
              <el-input type="textarea" v-model="roleConfig.character_setting" placeholder="请输入人设" />
            </el-form-item>
          </div>
          <el-button type="primary" plain size="mini" @click="addRoleConfig(newNode)" style="margin-top: 10px;">
            添加角色配置
          </el-button>
        </el-form-item>

        <!-- 进度指标管理 (v2.0) -->
        <el-divider>进度指标管理</el-divider>
        <el-form-item label="进度指标列表">
          <div v-for="(proc, pIdx) in newNode.process" :key="pIdx" style="border: 1px solid #409EFF; padding: 15px; margin-bottom: 15px; border-radius: 4px; background: #f0f9ff;">
            <!-- 紧凑模式 -->
            <div v-if="!proc._editing" style="display: flex; align-items: center; justify-content: space-between;">
              <span style="flex: 1; color: #409EFF; font-size: 13px;">进度指标{{pIdx + 1}}: {{ formatProcessSummary(proc) }}</span>
              <div>
                <el-button type="primary" size="mini" @click="$set(proc, '_editing', true)">编辑</el-button>
                <el-button type="danger" size="mini" @click="removeProcess(newNode, pIdx)">删除</el-button>
              </div>
            </div>
            <!-- 展开模式 -->
            <template v-else>
              <div style="display: flex; align-items: center; margin-bottom: 10px;">
                <span style="font-weight: bold; margin-right: 10px; color: #409EFF;">进度指标 {{pIdx + 1}}</span>
                <el-button size="mini" @click="$set(proc, '_editing', false)">收起</el-button>
                <el-button type="danger" size="mini" @click="removeProcess(newNode, pIdx)">删除</el-button>
              </div>

              <el-row :gutter="20">
                <el-col :span="6">
                  <el-form-item label="指标名称">
                    <el-input v-model="proc.name" placeholder="如：愤怒值" />
                  </el-form-item>
                </el-col>
                <el-col :span="6">
                  <el-form-item label="起始值">
                    <el-input-number v-model="proc.start_process" :min="0" style="width: 100%;" />
                  </el-form-item>
                </el-col>
                <el-col :span="6">
                  <el-form-item label="目标值">
                    <el-input-number v-model="proc.end_process" :min="0" style="width: 100%;" />
                  </el-form-item>
                </el-col>
                <el-col :span="6">
                  <el-form-item label="绑定结局">
                    <el-select v-model="proc.ending_name" placeholder="选择结局（可选）" clearable style="width: 100%;">
                      <el-option v-for="name in getEndingList(newNode)" :key="name" :label="name" :value="name" />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>

              <!-- 成就/道具/事件/好感度/属性奖励 -->
              <el-form-item label="成就/道具/事件/好感度/属性">
                <div v-for="(award, aIdx) in proc.award_conf" :key="aIdx" style="border: 1px solid #ccc; padding: 10px; margin-bottom: 8px; border-radius: 4px; background: white;">
                  <el-row :gutter="10">
                    <el-col :span="6">
                      <el-select v-model="award.type" placeholder="奖励类型" style="width: 100%;">
                        <el-option label="成就" value="成就" />
                        <el-option label="道具" value="道具" />
                        <el-option label="事件" value="事件" />
                        <el-option label="好感度" value="好感度" />
                        <el-option label="属性" value="属性" />
                      </el-select>
                    </el-col>
                    <template v-if="award.type === '成就' || award.type === '道具' || award.type === '事件'">
                      <el-col :span="16">
                        <el-input v-model="award.reward_description" placeholder="奖励描述" />
                      </el-col>
                    </template>
                    <template v-else-if="award.type === '好感度'">
                      <el-col :span="8">
                        <el-select v-model="award.role_id" placeholder="角色" style="width: 100%;">
                          <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                        </el-select>
                      </el-col>
                      <el-col :span="8">
                        <el-input-number v-model="award.affection_value" placeholder="好感度值" style="width: 100%;" />
                      </el-col>
                    </template>
                    <template v-else-if="award.type === '属性'">
                      <el-col :span="8">
                        <el-select v-model="award.attribute_name" placeholder="属性名称" style="width: 100%;">
                          <el-option v-for="attr in attributeNameList" :key="attr" :label="attr" :value="attr" />
                        </el-select>
                      </el-col>
                      <el-col :span="8">
                        <el-input-number v-model="award.attribute_value" placeholder="属性值" style="width: 100%;" />
                      </el-col>
                    </template>
                    <el-col :span="2">
                      <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeAwardConf(proc, aIdx)" />
                    </el-col>
                  </el-row>
                </div>
                <el-button type="primary" plain size="mini" @click="addAwardConf(proc)">添加奖励</el-button>
              </el-form-item>
            </template>
          </div>
          <el-button type="success" plain @click="addProcess(newNode)">添加新进度指标</el-button>
        </el-form-item>

        <!-- 用户消息处理 (v2.0) -->
        <el-divider>用户消息处理</el-divider>
        <el-form-item label="用户消息列表">
          <div v-if="newNode.user_messages_new && newNode.user_messages_new.length > 0" style="color: #409EFF; margin-bottom: 10px; font-size: 12px;">
            当前共 {{newNode.user_messages_new.length}} 条消息
          </div>
          <div v-for="(msg, msgIdx) in newNode.user_messages_new" :key="'msg-' + msgIdx" style="border: 1px solid #66b1ff; padding: 15px; margin-bottom: 10px; border-radius: 4px; background: #ecf5ff;">
            <div style="display: flex; align-items: center; margin-bottom: 10px;">
              <span style="font-weight: bold; margin-right: 10px;">消息 {{msgIdx + 1}}</span>
              <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeUserMessage(newNode, msgIdx)">删除</el-button>
            </div>

            <el-form-item label="如果用户发的消息">
              <el-input type="textarea" v-model="msg.result" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="例如：用户提到了某个关键词..." />
            </el-form-item>

            <el-form-item label="则（AI回复方式）">
              <el-input type="textarea" v-model="msg.ai_reply" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="例如：AI应该如何回应..." />
            </el-form-item>

            <el-form-item label="进度指标名称">
              <el-select v-model="msg.progress_name" placeholder="选择进度指标（可选）" clearable style="width: 100%;">
                <el-option label="无" value="" />
                <el-option
                  v-for="proc in getProcessList(newNode)"
                  :key="proc.name"
                  :label="proc.name"
                  :value="proc.name"
                />
              </el-select>
            </el-form-item>

            <el-form-item label="实化数值">
              <el-input-number v-model="msg.progress_value" placeholder="进度增减值" style="width: 100%;" />
            </el-form-item>

            <!-- 用户消息输出示例 -->
            <el-form-item label="输出示例">
              <div v-for="(ex, exIdx) in msg.output_examples" :key="'numoe-' + exIdx" style="display: flex; align-items: center; margin-bottom: 4px;">
                <el-input type="textarea" v-model="ex.content" :rows="2" placeholder="输出示例内容" style="flex: 1; margin-right: 8px;" />
                <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeUserMsgOutputExample(msg, exIdx)" />
              </div>
              <el-button type="primary" plain size="mini" @click="addUserMsgOutputExample(msg)">添加输出示例</el-button>
            </el-form-item>
          </div>
          <el-button type="primary" plain size="mini" @click="addUserMessage(newNode)" style="margin-top: 10px;">
            添加用户消息处理
          </el-button>
        </el-form-item>

        <!-- 彩蛋管理 (v2.0) - 移到用户消息后面 -->
        <el-divider>彩蛋管理</el-divider>
        <el-form-item label="彩蛋列表">
          <div v-for="(egg, eIdx) in newNode.egg" :key="eIdx" style="border: 1px solid #67C23A; padding: 15px; margin-bottom: 15px; border-radius: 4px; background: #f0f9f0;">
            <!-- 紧凑模式 -->
            <div v-if="!egg._editing" style="display: flex; align-items: center; justify-content: space-between;">
              <span style="flex: 1; color: #67C23A; font-size: 13px;">彩蛋{{eIdx + 1}}: {{ formatEggSummary(egg) }}</span>
              <div>
                <el-button type="primary" size="mini" @click="$set(egg, '_editing', true)">编辑</el-button>
                <el-button type="danger" size="mini" @click="removeEgg(newNode, eIdx)">删除</el-button>
              </div>
            </div>
            <!-- 展开模式 -->
            <template v-else>
              <div style="display: flex; align-items: center; margin-bottom: 10px;">
                <span style="font-weight: bold; margin-right: 10px; color: #67C23A;">彩蛋 {{eIdx + 1}}</span>
                <el-button size="mini" @click="$set(egg, '_editing', false)">收起</el-button>
                <el-button type="danger" size="mini" @click="removeEgg(newNode, eIdx)">删除</el-button>
              </div>

              <el-form-item label="彩蛋名称">
                <el-input v-model="egg.egg_name" placeholder="请输入彩蛋名称" />
              </el-form-item>

              <el-form-item label="触发条件">
                <el-input type="textarea" v-model="egg.trigger_condition" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入触发条件描述..." />
              </el-form-item>

              <!-- 进度增益 -->
              <el-form-item label="进度增益">
                <div v-if="!newNode.process || !Array.isArray(newNode.process) || newNode.process.length === 0" style="color: #999; margin-bottom: 10px;">
                  请先添加进度指标，才能配置进度增益
                </div>
                <div v-for="(pg, pgIdx) in egg.process" :key="pgIdx" style="border: 1px solid #ccc; padding: 10px; margin-bottom: 8px; border-radius: 4px; background: white;">
                  <el-row :gutter="10">
                    <el-col :span="10">
                      <el-select v-model="pg.process_name" placeholder="选择进度指标" style="width: 100%;">
                        <el-option
                          v-for="proc in newNode.process"
                          :key="proc.name"
                          :label="proc.name"
                          :value="proc.name"
                        />
                      </el-select>
                    </el-col>
                    <el-col :span="12">
                      <el-input-number v-model="pg.process_gain" placeholder="增益值（可为负数）" style="width: 100%;" />
                    </el-col>
                    <el-col :span="2">
                      <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeProcessGain(egg, pgIdx)" />
                    </el-col>
                  </el-row>
                </div>
                <el-button type="primary" plain size="mini" @click="addProcessGain(egg, newNode)" :disabled="!newNode.process || !Array.isArray(newNode.process) || newNode.process.length === 0">添加进度增益</el-button>
              </el-form-item>

              <!-- 成就/道具/事件/好感度/属性奖励 -->
              <el-form-item label="成就/道具/事件/好感度/属性">
                <div v-for="(award, aIdx) in egg.award_conf" :key="aIdx" style="border: 1px solid #ccc; padding: 10px; margin-bottom: 8px; border-radius: 4px; background: white;">
                  <el-row :gutter="10">
                    <el-col :span="6">
                      <el-select v-model="award.type" placeholder="奖励类型" style="width: 100%;">
                        <el-option label="成就" value="成就" />
                        <el-option label="道具" value="道具" />
                        <el-option label="事件" value="事件" />
                        <el-option label="好感度" value="好感度" />
                        <el-option label="属性" value="属性" />
                      </el-select>
                    </el-col>
                    <template v-if="award.type === '成就' || award.type === '道具' || award.type === '事件'">
                      <el-col :span="16">
                        <el-input v-model="award.reward_description" placeholder="奖励描述" />
                      </el-col>
                    </template>
                    <template v-else-if="award.type === '好感度'">
                      <el-col :span="8">
                        <el-select v-model="award.role_id" placeholder="角色" style="width: 100%;">
                          <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
                        </el-select>
                      </el-col>
                      <el-col :span="8">
                        <el-input-number v-model="award.affection_value" placeholder="好感度值" style="width: 100%;" />
                      </el-col>
                    </template>
                    <template v-else-if="award.type === '属性'">
                      <el-col :span="8">
                        <el-select v-model="award.attribute_name" placeholder="属性名称" style="width: 100%;">
                          <el-option v-for="attr in attributeNameList" :key="attr" :label="attr" :value="attr" />
                        </el-select>
                      </el-col>
                      <el-col :span="8">
                        <el-input-number v-model="award.attribute_value" placeholder="属性值" style="width: 100%;" />
                      </el-col>
                    </template>
                    <el-col :span="2">
                      <el-button type="danger" size="mini" icon="el-icon-delete" @click="removeEggAwardConf(egg, aIdx)" />
                    </el-col>
                  </el-row>
                </div>
                <el-button type="primary" plain size="mini" @click="addEggAwardConf(egg)">添加奖励</el-button>
              </el-form-item>
            </template>
          </div>
          <el-button type="success" plain @click="addEgg(newNode)">添加新彩蛋</el-button>
        </el-form-item>
      </div>

      <el-form-item>
        <el-button type="success" @click="createNodeData">添加</el-button>
      </el-form-item>
    </el-form>

    <!-- 节点移动对话框 -->
    <el-dialog
      title="选择插入位置"
      :visible.sync="moveDialogVisible"
      width="500px"
      @close="closeMoveDialog"
    >
      <div v-if="moveDialogNode" style="margin-bottom: 15px; padding: 10px; background: #f5f7fa; border-radius: 4px;">
        <strong>移动节点：</strong>{{ moveDialogNode.title }}
      </div>
      <div style="max-height: 400px; overflow-y: auto;">
        <el-radio-group v-model="selectedMovePosition" style="display: flex; flex-direction: column;">
          <el-radio 
            v-for="(n, idx) in moveDialogOtherNodes" 
            :key="n.id"
            :label="idx"
            style="margin: 8px 0; padding: 10px; border: 1px solid #e4e7ed; border-radius: 4px;"
          >
            移动到「<strong>{{ n.title }}</strong>」之前
          </el-radio>
        </el-radio-group>
        <div v-if="moveDialogOtherNodes.length === 0" style="text-align: center; color: #999; padding: 20px;">
          没有其他节点可选
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="moveDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmMove" :disabled="selectedMovePosition === null">确定</el-button>
      </span>
    </el-dialog>

    <!-- AI扩写结果抽屉 -->
    <el-drawer
      title="AI 扩写结果"
      :visible.sync="expandDrawerVisible"
      direction="rtl"
      size="45%"
      :destroy-on-close="false"
    >
      <div style="padding: 0 20px 20px;">
        <!-- 加载中 -->
        <div v-if="expandDrawerNodeId && expandStates[expandDrawerNodeId] && expandStates[expandDrawerNodeId].loading" style="text-align: center; padding: 20px;">
          <i class="el-icon-loading" style="font-size: 24px;"></i>
          <p style="margin-top: 8px; color: #909399;">正在生成中...</p>
        </div>

        <!-- 流式输出（加载中且尚无解析结果时显示原始文本） -->
        <div v-if="expandDrawerNodeId && expandStates[expandDrawerNodeId] && expandStates[expandDrawerNodeId].text && expandStates[expandDrawerNodeId].loading && !expandStates[expandDrawerNodeId].result" class="expand-stream-output">
          <pre style="white-space: pre-wrap; word-break: break-word; background: #f5f7fa; padding: 16px; border-radius: 6px; font-size: 13px; line-height: 1.6; max-height: 70vh; overflow-y: auto;">{{ expandStates[expandDrawerNodeId].text }}</pre>
        </div>

        <!-- 结构化结果展示 -->
        <div v-if="expandDrawerNodeId && expandStates[expandDrawerNodeId] && expandStates[expandDrawerNodeId].result" class="expand-result-structured" style="max-height: 70vh; overflow-y: auto;">
          <template v-for="section in formatExpandResult(expandStates[expandDrawerNodeId].result)">
            <div :key="section.label" class="expand-result-section">
              <div class="expand-result-label">{{ section.label }}</div>
              <pre class="expand-result-content">{{ section.content }}</pre>
            </div>
          </template>
        </div>

        <!-- 暂无结果 -->
        <div v-if="expandDrawerNodeId && (!expandStates[expandDrawerNodeId] || (!expandStates[expandDrawerNodeId].loading && !expandStates[expandDrawerNodeId].text && !expandStates[expandDrawerNodeId].result))" style="text-align: center; padding: 40px; color: #c0c4cc;">
          <i class="el-icon-document" style="font-size: 40px;"></i>
          <p>暂无扩写结果</p>
        </div>

        <!-- 操作按钮 -->
        <div v-if="expandDrawerNodeId && expandStates[expandDrawerNodeId] && !expandStates[expandDrawerNodeId].loading && (expandStates[expandDrawerNodeId].text || expandStates[expandDrawerNodeId].result)" style="text-align: center; margin-top: 16px;">
          <el-button type="primary" @click="applyExpandResultToNode" icon="el-icon-check" :disabled="!expandStates[expandDrawerNodeId].result">应用到表单并保存</el-button>
          <el-button type="warning" plain @click="reExpand" icon="el-icon-refresh">重新生成</el-button>
        </div>
      </div>
    </el-drawer>

    <!-- AI局部调整对话框 -->
    <el-dialog
      :title="'AI调整 - ' + refineSectionLabel"
      :visible.sync="refineDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form label-position="top">
        <el-form-item label="修改建议（告诉AI你想怎么改）">
          <el-input
            type="textarea"
            v-model="refineSuggestion"
            :autosize="{ minRows: 3, maxRows: 6 }"
            placeholder="例如：这段太生硬了，需要更口语化、更有趣..."
          />
        </el-form-item>
      </el-form>

      <!-- 流式输出预览 -->
      <div v-if="activeRefineState && (activeRefineState.loading || activeRefineState.streamText || activeRefineState.result || activeRefineState.error)" style="margin-top: 12px;">
        <div style="font-size: 12px; color: #909399; margin-bottom: 8px;">
          <i v-if="activeRefineState.loading" class="el-icon-loading"></i>
          {{ activeRefineState.loading ? '正在生成...' : (activeRefineState.result ? '生成完成' : '上次任务已结束') }}
        </div>
        <pre v-if="activeRefineState.streamText" style="white-space: pre-wrap; word-break: break-word; background: #f5f7fa; padding: 12px; border-radius: 6px; font-size: 13px; line-height: 1.6; max-height: 300px; overflow-y: auto;">{{ activeRefineState.streamText }}</pre>
        <div v-if="activeRefineState.error" style="margin-top: 8px; color: #f56c6c; font-size: 12px;">{{ activeRefineState.error }}</div>
      </div>

      <span slot="footer">
        <el-button @click="refineDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="doRefine" :loading="!!activeRefineState && activeRefineState.loading" :disabled="!refineSuggestion.trim() || (!!activeRefineState && activeRefineState.loading)">开始调整</el-button>
        <el-button v-if="activeRefineState && activeRefineState.result && !activeRefineState.loading" type="success" @click="applyRefineResult">应用结果</el-button>
      </span>
    </el-dialog>

    <!-- 角色管理弹窗 -->
    <el-dialog title="角色管理" :visible.sync="showRoleDialog" width="85%" top="5vh" destroy-on-close>
      <RoleList v-if="showRoleDialog" :game="game" />
    </el-dialog>

    <!-- 属性管理弹窗 -->
    <el-dialog title="属性管理" :visible.sync="showAttributeDialog" width="70%" top="5vh" destroy-on-close>
      <AttributeManager v-if="showAttributeDialog" :game="game" />
    </el-dialog>

    <!-- 商城管理弹窗 -->
    <el-dialog title="商城管理" :visible.sync="showShopDialog" width="85%" top="5vh" destroy-on-close>
      <ShopItems v-if="showShopDialog" :game="game" />
    </el-dialog>

    <!-- 衣橱管理弹窗 -->
    <el-dialog title="衣橱管理" :visible.sync="showCostumeDialog" width="85%" top="5vh" destroy-on-close>
      <CostumeShop v-if="showCostumeDialog" :game="game" />
    </el-dialog>

    <!-- 复制节点弹窗 -->
    <CopyNodeDialog
      :show.sync="showCopyDialog"
      :chapters="chapters"
      :sourceNode="copySourceNode"
      :gameId="game.id"
      :currentChapterNodes="nodes"
      @confirm="handleCopyConfirm"
    />

    </template>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch} from 'vue-property-decorator'
import draggable from 'vuedraggable'
import RoleList from '@/views/Role.vue'
import AttributeManager from '@/components/management/AttributeManager.vue'
import ShopItems from '@/views/ShopItems.vue'
import CostumeShop from '@/views/CostumeShop.vue'
import { getToken } from '@/utils/auth'
import {
  getNodes,
  getNode,
  updateNode,
  deleteNode,
  createNode,
  GameNode,
  changeNodeOrder,
  getChapters,
  Chapter,
  RoleConfig,
  EntryCondition,
  PlotEnding
} from '@/api/node'
import {getRoles, Role} from "@/api/role";
import {Game, saveGame, getModels, Model} from "@/api/game";
import {getGamePoints, GamePoint} from "@/api/gamePoint";
import {getCostumeList, Costume} from "@/api/costume";
import CopyNodeDialog from '@/components/node-editor/CopyNodeDialog.vue';
import {GameAttribute, getGameAttributes} from "@/api/gameAttribute";
import {expandFixedNodeStream, expandAINodeStream, FixedNodeResponse, AINodeResponse, refineSectionStream, RefineSectionType, RefineSectionResponse} from "@/api/expand";

type RefineTaskState = {
  key: string
  nodeId: number
  section: RefineSectionType
  label: string
  branchIndex: number | null
  indicatorIndex: number | null
  eggIndex: number | null
  suggestion: string
  streamText: string
  result: string
  loading: boolean
  error: string
}

@Component({
  components: { draggable, RoleList, AttributeManager, ShopItems, CostumeShop, CopyNodeDialog }
})
export default class NodeDetailV2 extends Vue {
  @Prop({ required: true }) readonly game!: Game

  showRoleDialog = false
  showAttributeDialog = false
  showShopDialog = false
  showCostumeDialog = false

  nodes: GameNode[] = []
  newNode: GameNode = {
    title: '',
    description: '',
    role_ids: [],
    prologue: '',
    node_type: "",
    dialogues: [],
    expanded: true,
    game_id: this.game?.id,
    bg_url: '',
    music_url: '',
    game_chapter_id: undefined,
    user_special: [],
    story_background: '',
    hidden_ui: false,
    limit_turn: undefined,
    node_task: '',
    image_prompt: '',
    user_messages_new: [],
    process: [],
    egg: [],
    output_examples: [],
    dialogue_text: '',
    entry_conditions: [],
    current_plot_new: {
      plot_start: '',
      plot_process: '',
      plot_end: [] as PlotEnding[]
    }
  }

  nodeTypeOptions = [
    { label: 'AI场景', value: 'ai' },
    { label: '固定剧情场景', value: 'fixed' }
  ]

  roles: Role[] = []
  chapters: Chapter[] = []
  gamePoints: GamePoint[] = []
  costumesCache: { [roleId: number]: Costume[] } = {} // roleId -> costumes
  uploadUrl = '/api_v2/upload'  // Go 后端 OSS 上传
  uploading = false
  models: Model[] = []
  gameAttributes: GameAttribute[] = []

  // 复制节点对话框
  showCopyDialog = false
  copySourceNode: GameNode | null = null

  get attributeNameList(): string[] {
    if (this.gameAttributes.length === 0) return ['智慧值', '灵敏值', '精神值', '圣母值', '黑化值']
    return [...new Set(this.gameAttributes.map(a => a.name))]
  }

  async loadGameAttributes() {
    if (this.game?.id) {
      try {
        this.gameAttributes = await getGameAttributes(this.game.id)
      } catch { /* use defaults */ }
    }
  }

  // 上传请求头（带token）
  get uploadHeaders() {
    const token = getToken()
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  // AI扩写抽屉相关
  expandDrawerVisible = false
  expandDrawerNodeId: number | null = null  // 当前抽屉显示的节点ID
  expandStates: Record<number, { text: string; loading: boolean; result: any }> = {}

  // AI局部调整相关
  refineDialogVisible = false
  refineDialogKey = ''
  refineStates: Record<string, RefineTaskState> = {}
  refineSuggestion = ''
  refineSection: RefineSectionType = 'image_prompt'
  refineNodeId: number | null = null
  refineBranchIndex: number | null = null
  refineIndicatorIndex: number | null = null
  refineEggIndex: number | null = null
  refineSectionLabel = ''

  get activeRefineState(): RefineTaskState | null {
    if (!this.refineDialogKey) return null
    return this.refineStates[this.refineDialogKey] || null
  }

  // 节点移动对话框相关
  moveDialogVisible = false
  moveDialogNode: GameNode | null = null
  moveDialogOtherNodes: GameNode[] = []
  selectedMovePosition: number | null = null

  // 游戏标签和连载状态
  gameTags: string[] = []
  tagInputVisible = false
  tagInputValue = ''
  gameCategory: string[] = []
  categoryInputVisible = false
  categoryInputValue = ''
  gameSerialStatus = 0
  gameHeatBoost = 0

  @Watch("game", {immediate: true})
  onGameChange(newGame: Game){
    if (newGame && newGame.id) {
      this.loadData()
      // 解析游戏标签、分类和连载状态
      try {
        this.gameTags = JSON.parse(newGame.tags || '[]')
      } catch {
        this.gameTags = []
      }
      this.gameCategory = newGame.category || []
      this.gameSerialStatus = newGame.serial_status || 0
      this.gameHeatBoost = newGame.heat_boost || 0
    }
  }

  loadData() {
    let gameId = this.game.id
    console.log('开始加载数据, game_id:', gameId)

    // 加载游戏属性
    this.loadGameAttributes()

    // 加载积分类型列表
    getGamePoints(gameId).then((res) => {
      console.log('getGamePoints 响应:', res)
      if (res.data && Array.isArray(res.data)) {
        this.gamePoints = res.data
        console.log('加载积分类型成功, 数量:', this.gamePoints.length, '数据:', this.gamePoints)
      } else {
        console.warn('积分类型数据格式不正确:', res)
      }
    }).catch((error) => {
      console.error('加载积分类型失败:', error)
      if (error.response) {
        console.error('响应状态:', error.response.status)
        console.error('响应数据:', error.response.data)
      }
    })
    
    // 先加载角色列表，再加载节点（这样转换dialogue_text时能找到角色名）
    getRoles(this.game.id).then((res)=>{
      this.roles = res
      
      // 角色加载完成后再加载节点
      return getNodes(gameId)
    }).then((res) => {
      console.log('NodeDetailV2 加载节点数据:', res)
      let nodes: GameNode[] = []
      // 添加防御性检查
      if (res.data && Array.isArray(res.data)) {
        for (let i of res.data){
          i.expanded = false
          // 确保字段被正确初始化
          if (i.node_type === 'ai') {
            if (!i.user_special || !Array.isArray(i.user_special)) this.$set(i, 'user_special', [])
            if (!i.story_background) this.$set(i, 'story_background', '')
            if (i.hidden_ui === undefined) this.$set(i, 'hidden_ui', false)

            // 校验并修正 current_plot_new 格式
            const isValidPlotNew = i.current_plot_new
              && typeof i.current_plot_new === 'object'
              && 'plot_start' in i.current_plot_new
              && 'plot_process' in i.current_plot_new
              && 'plot_end' in i.current_plot_new

            if (!isValidPlotNew) {
              this.$set(i, 'current_plot_new', { plot_start: '', plot_process: '', plot_end: [] })
            } else {
              // plot_end 向后兼容：旧数据 plot_end 是 string → 转为数组
              const plotNew = i.current_plot_new!
              if (typeof plotNew.plot_end === 'string') {
                const old = plotNew.plot_end
                plotNew.plot_end = old.trim()
                  ? [{ name: '结局1', content: old, output_examples: [] }]
                  : []
              }
            }

            // 初始化 image_prompt
            if (!i.image_prompt) this.$set(i, 'image_prompt', '')

            // 初始化 v2.0 字段
            if (!i.process) this.$set(i, 'process', [])
            if (!i.egg) this.$set(i, 'egg', [])
            if (!i.user_messages_new) this.$set(i, 'user_messages_new', [])
            // user_messages_new 兼容处理
            if (i.user_messages_new && Array.isArray(i.user_messages_new)) {
              i.user_messages_new.forEach((msg: any) => {
                msg.progress_name = msg.progress_name || ''
                if (!msg.output_examples) msg.output_examples = []
                delete msg.egg_name
              })
            }
            if (!i.output_examples) this.$set(i, 'output_examples', [])
          }
          // 初始化进入条件
          if (!i.entry_conditions) this.$set(i, 'entry_conditions', [])
          // 数据兼容性处理：将旧的英文类型值转换为中文
          if (i.entry_conditions && i.entry_conditions.length > 0) {
            i.entry_conditions = this.migrateEntryConditionTypes(i.entry_conditions)
          }
          // 如果是固定节点，将dialogues转换为dialogue_text（此时roles已加载）
          if (i.node_type === 'fixed' && i.dialogues && Array.isArray(i.dialogues)) {
            this.$set(i, 'dialogue_text', this.dialoguesToText(i.dialogues))
          }
          nodes.push(i as GameNode)
        }
      } else {
        console.error('getNodes返回的数据格式不正确:', res)
      }
      this.nodes = nodes
      // 预加载服装条件中涉及的角色服装列表
      this.preloadCostumesForEntryConditions()
      if (res.message) {
        this.$notify({
          title: '提示',
          message: res.message,
          type: 'warning',
          position: 'top-right',
          showClose: true,
          duration: 2000
        })
      }
    })
    .catch((error) => {
      console.error('加载节点失败:', error)
      this.$message.error('加载节点失败: ' + (error.message || '未知错误'))
    })

    getChapters(this.game.id).then((res)=>{
      this.chapters = res.data
    })

    getModels().then((res) => {
      this.models = res.data
    })
  }

  updateNodeData(node: GameNode) {
    if (!node.id) return

    // 校验结局名称唯一性
    if (node.current_plot_new && Array.isArray(node.current_plot_new.plot_end)) {
      const names = (node.current_plot_new.plot_end as PlotEnding[]).map(e => e.name).filter(n => n)
      const uniqueNames = new Set(names)
      if (names.length !== uniqueNames.size) {
        this.$message.error('结局名称不能重复')
        return
      }
    }

    // 确保 version 字段存在
    if (!node.version) {
      this.$set(node, 'version', 'v2.0')
    }

    const nodeData = JSON.parse(JSON.stringify(node))
    delete nodeData.expanded
    delete nodeData._autoCompleting

    // 如果是固定节点，将dialogue_text转换为dialogues
    if (node.node_type === 'fixed' && node.dialogue_text !== undefined) {
      nodeData.dialogues = this.textToDialogues(node.dialogue_text)
      delete nodeData.dialogue_text  // 不发送dialogue_text到后端
    }

    // 过滤进入条件字段
    if (nodeData.entry_conditions) {
      nodeData.entry_conditions = this.filterEntryConditions(nodeData.entry_conditions)
    }

    // 清除 process 和 egg 的 _editing 临时字段
    if (nodeData.process && Array.isArray(nodeData.process)) {
      nodeData.process.forEach((p: any) => delete p._editing)
    }
    if (nodeData.egg && Array.isArray(nodeData.egg)) {
      nodeData.egg.forEach((e: any) => delete e._editing)
    }

    updateNode(node.id, nodeData).then(() => {
      this.$message.success('保存成功')
      // 保存成功后收起节点
      node.expanded = false
      // 重新加载列表数据
      this.loadData()
    }).catch((error) => {
      console.error('保存节点失败:', error)
      this.$message.error('保存失败: ' + (error.response?.data?.message || error.message))
    })
  }

  deleteNodeData(id?: number) {
    if (!id) return
    this.$confirm('确定删除该节点吗？', '提示', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      deleteNode(id).then(() => {
        this.$message.success('删除成功')
        this.loadData()
      })
    })
  }

  onNewNodeTypeChange(val: string) {
    if (val === 'ai' && (!this.newNode.entry_conditions || this.newNode.entry_conditions.length === 0)) {
      this.newNode.entry_conditions = [{ type: '货币', value: 12, is_free: false }]
    }
  }

  createNodeData() {
    // 校验结局名称唯一性
    if (this.newNode.current_plot_new && Array.isArray(this.newNode.current_plot_new.plot_end)) {
      const names = (this.newNode.current_plot_new.plot_end as PlotEnding[]).map(e => e.name).filter(n => n)
      const uniqueNames = new Set(names)
      if (names.length !== uniqueNames.size) {
        this.$message.error('结局名称不能重复')
        return
      }
    }

    this.newNode.game_id = this.game.id
    this.newNode.version = "v2.0"  // 标记为v2版本
    const nodeData = JSON.parse(JSON.stringify(this.newNode))
    delete nodeData.expanded
    delete nodeData._autoCompleting

    // 如果是固定节点，将dialogue_text转换为dialogues
    if (this.newNode.node_type === 'fixed' && this.newNode.dialogue_text !== undefined) {
      nodeData.dialogues = this.textToDialogues(this.newNode.dialogue_text)
      delete nodeData.dialogue_text  // 不发送dialogue_text到后端
    }

    // 过滤进入条件字段
    if (nodeData.entry_conditions) {
      nodeData.entry_conditions = this.filterEntryConditions(nodeData.entry_conditions)
    }

    // 清除 process 和 egg 的 _editing 临时字段
    if (nodeData.process && Array.isArray(nodeData.process)) {
      nodeData.process.forEach((p: any) => delete p._editing)
    }
    if (nodeData.egg && Array.isArray(nodeData.egg)) {
      nodeData.egg.forEach((e: any) => delete e._editing)
    }

    createNode(nodeData).then(() => {
      this.$message.success('新增成功')
      this.newNode = {
        title: '',
        description: '',
        role_ids: [],
        prologue: '',
        dialogues: [],
        node_type: "",
        expanded: true,
        game_id: this.game.id,
        bg_url: '',
        music_url: '',
        game_chapter_id: undefined,
        user_special: [],
        story_background: '',
        hidden_ui: false,
        limit_turn: undefined,
        node_task: '',
        image_prompt: '',
        user_messages_new: [],
        process: [],
        egg: [],
        output_examples: [],
        dialogue_text: '',
        entry_conditions: [],
        current_plot_new: {
          plot_start: '',
          plot_process: '',
          plot_end: [] as PlotEnding[]
        }
      }
      this.loadData()
    }).catch((error) => {
      console.error('创建节点失败:', error)
      this.$message.error('创建失败: ' + (error.response?.data?.message || error.message))
    })
  }

  onDragEnd() {
    let node_ids = this.nodes.map(n => n.id)
    changeNodeOrder(node_ids)
  }

  saveGame() {
    // 将标签、分类、连载状态、热度加成写入game对象
    this.game.tags = JSON.stringify(this.gameTags)
    this.game.category = this.gameCategory
    this.game.serial_status = this.gameSerialStatus
    this.game.heat_boost = this.gameHeatBoost
    saveGame(this.game.id, this.game);
    console.log('保存故事概要:', this.game)
    this.$message.success('故事概要已保存')
  }

  handleTagClose(tag: string) {
    this.gameTags.splice(this.gameTags.indexOf(tag), 1)
  }

  showTagInput() {
    this.tagInputVisible = true
    this.$nextTick(() => {
      (this.$refs.tagInput as any).$refs.input.focus()
    })
  }

  handleTagConfirm() {
    if (this.tagInputValue) {
      if (!this.gameTags.includes(this.tagInputValue)) {
        this.gameTags.push(this.tagInputValue)
      }
      this.tagInputValue = ''
    }
    this.tagInputVisible = false
  }

  handleCategoryClose(cat: string) {
    this.gameCategory.splice(this.gameCategory.indexOf(cat), 1)
  }

  showCategoryInput() {
    this.categoryInputVisible = true
    this.$nextTick(() => {
      (this.$refs.categoryInput as any).$refs.input.focus()
    })
  }

  handleCategoryConfirm() {
    if (this.categoryInputValue) {
      if (!this.gameCategory.includes(this.categoryInputValue)) {
        this.gameCategory.push(this.categoryInputValue)
      }
      this.categoryInputValue = ''
    }
    this.categoryInputVisible = false
  }

  handleCoverUploadSuccess(res: any) {
    this.uploading = false
    if (res && res.url) {
      this.$set(this.game, 'url', res.url)
    }
  }

  handleUploadSuccess(res: any, node: GameNode, field: 'bg_url' | 'music_url') {
    this.uploading = false
    if (res && res.url) {
      this.$set(node, field, res.url)
      if (field === 'bg_url') {
        if (res.id) this.$set(node, 'bg_resource_id', res.id)
        if (res.display_name) this.$set(node, 'bg_display_name', res.display_name)
      } else if (field === 'music_url') {
        if (res.id) this.$set(node, 'music_resource_id', res.id)
        if (res.display_name) this.$set(node, 'music_display_name', res.display_name)
      }
    }
  }

  getFileName(url: string): string {
    if (!url) return ''
    try {
      const u = new URL(url, window.location.origin)
      const path = u.pathname
      const base = path.substring(path.lastIndexOf('/') + 1)
      return decodeURIComponent(base || '')
    } catch (e) {
      const parts = url.split('?')[0].split('#')[0].split('/')
      return decodeURIComponent(parts[parts.length - 1] || '')
    }
  }

  playAudio(nodeId: number|undefined) {
    if (nodeId == undefined) return
    const audioRefs = this.$refs.audioPlayerRef as HTMLAudioElement[] | HTMLAudioElement
    const audioList = Array.isArray(audioRefs) ? audioRefs : [audioRefs]

    for (let audioEl of audioList) {
      if (audioEl?.dataset?.id == String(nodeId)) {
        audioEl.play()
      } else {
        audioEl.pause?.()
      }
    }
  }

  addRoleConfig(node: GameNode) {
    if (!node.user_special) {
      this.$set(node, 'user_special', [])
    }
    (node.user_special as RoleConfig[]).push({
      role_id: this.roles.length > 0 ? this.roles[0].id : undefined,
      task: '',
      speak_timing: '',
      speak_tone: '',
      character_setting: ''
    })
  }

  removeRoleConfig(node: GameNode, index: number) {
    if (node.user_special) {
      node.user_special.splice(index, 1)
    }
  }

  addDialogue(node: GameNode) {
    if (this.roles.length != 0){
      if (node.dialogues == undefined){
        node.dialogues = []
      }
      node.dialogues.push({ role_id: this.roles[0].id, text: ''})
    }
  }

  removeDialogue(node: GameNode, didx: number) {
    node.dialogues.splice(didx, 1)
  }

  toggleNodeExpanded(node: GameNode) {
    node.expanded = !node.expanded
    if (node.expanded) {
      if (node.node_type === 'ai') {
        if (!node.user_special) this.$set(node, 'user_special', [])
        if (!node.story_background) this.$set(node, 'story_background', '')
        if (node.hidden_ui === undefined) this.$set(node, 'hidden_ui', false)
        if (!node.current_plot_new) this.$set(node, 'current_plot_new', { plot_start: '', plot_process: '', plot_end: [] })
        if (!node.image_prompt) this.$set(node, 'image_prompt', '')

        // 初始化进度指标和彩蛋数组（v2.0）
        if (!node.process) this.$set(node, 'process', [])
        if (!node.egg) this.$set(node, 'egg', [])
        if (!node.user_messages_new) this.$set(node, 'user_messages_new', [])
      } else if (node.node_type === 'fixed') {
        // 如果是固定节点，确保dialogue_text已同步
        if (!node.dialogue_text && node.dialogues) {
          this.$set(node, 'dialogue_text', this.dialoguesToText(node.dialogues))
        }
      }
    }
  }

  // ========== 结局管理 ==========
  addEnding(node: GameNode) {
    if (!node.current_plot_new) return
    if (!Array.isArray(node.current_plot_new.plot_end)) {
      this.$set(node.current_plot_new, 'plot_end', [])
    }
    const endings = node.current_plot_new.plot_end as PlotEnding[]
    endings.push({
      name: `结局${endings.length + 1}`,
      content: '',
      output_examples: []
    })
  }

  removeEnding(node: GameNode, index: number) {
    if (!node.current_plot_new || !Array.isArray(node.current_plot_new.plot_end)) return
    (node.current_plot_new.plot_end as PlotEnding[]).splice(index, 1)
  }

  addEndingOutputExample(ending: PlotEnding) {
    if (!ending.output_examples) {
      ending.output_examples = []
    }
    ending.output_examples.push({ content: '' })
  }

  removeEndingOutputExample(ending: PlotEnding, index: number) {
    if (ending.output_examples) {
      ending.output_examples.splice(index, 1)
    }
  }

  getEndingList(node: GameNode): string[] {
    if (!node.current_plot_new || !Array.isArray(node.current_plot_new.plot_end)) return []
    return (node.current_plot_new.plot_end as PlotEnding[])
      .map(e => e.name)
      .filter(n => n && n.trim() !== '')
  }

  // ========== 进度指标管理 ==========
  formatProcessSummary(proc: any): string {
    let parts: string[] = []
    parts.push(`${proc.name || '未命名'} (${proc.start_process ?? 0}~${proc.end_process ?? 100})`)
    if (proc.award_conf && proc.award_conf.length > 0) {
      proc.award_conf.forEach((a: any) => {
        if (a.type === '事件') parts.push(`事件【${a.reward_description || ''}】`)
        else if (a.type === '属性') parts.push(`属性【${a.attribute_name || ''}${a.attribute_value > 0 ? '+' : ''}${a.attribute_value || 0}】`)
        else if (a.type === '好感度') {
          const roleName = this.roles.find(r => r.id === a.role_id)?.name || ''
          parts.push(`好感度【${roleName}${a.affection_value > 0 ? '+' : ''}${a.affection_value || 0}】`)
        }
        else parts.push(`${a.type}【${a.reward_description || ''}】`)
      })
    }
    if (proc.ending_name) parts.push(`进入${proc.ending_name}`)
    return parts.join(', ')
  }

  addProcess(node: GameNode) {
    if (!node.process) {
      this.$set(node, 'process', [])
    }
    const processArray = node.process as any[];
    processArray.push({
      name: '',
      start_process: 0,
      end_process: 100,
      award_conf: [],
      points_reward: [],
      affection_reward: [],
      _editing: true
    })
  }

  removeProcess(node: GameNode, index: number) {
    if (node.process) {
      const processName = (node.process as any[])[index]?.name
      ;(node.process as any[]).splice(index, 1)
      
      // 只在有效名称时清空引用
      if (processName && processName.trim() !== '') {
        // 清空user_messages_new中引用该进度指标的地方
        if (node.user_messages_new && Array.isArray(node.user_messages_new)) {
          node.user_messages_new.forEach(msg => {
            if (msg.progress_name === processName) {
              msg.progress_name = ''
              msg.progress_value = 0
            }
          })
        }
        
        // 删除彩蛋中引用该进度指标的process_gain项
        if (node.egg && Array.isArray(node.egg)) {
          (node.egg as any[]).forEach(egg => {
            if (egg.process && Array.isArray(egg.process)) {
              // 过滤掉引用该进度指标的项
              egg.process = egg.process.filter((pg: any) => pg.process_name !== processName)
            }
          })
        }
      }
    }
  }

  addAwardConf(proc: any) {
    if (!proc.award_conf) {
      this.$set(proc, 'award_conf', [])
    }
    proc.award_conf.push({ type: '成就', reward_description: '' })
  }

  removeAwardConf(proc: any, index: number) {
    if (proc.award_conf) {
      proc.award_conf.splice(index, 1)
    }
  }

  addPointsReward(proc: any) {
    if (!proc.points_reward) {
      this.$set(proc, 'points_reward', [])
    }
    const defaultPointId = this.gamePoints.length > 0 ? this.gamePoints[0].id : 1
    proc.points_reward.push({ points_id: defaultPointId, points_gain: 0 })
  }

  removePointsReward(proc: any, index: number) {
    if (proc.points_reward) {
      proc.points_reward.splice(index, 1)
    }
  }

  addAffectionReward(proc: any) {
    if (!proc.affection_reward) {
      this.$set(proc, 'affection_reward', [])
    }
    proc.affection_reward.push({ 
      role_id: this.roles.length > 0 ? this.roles[0].id : undefined, 
      affection_gain: 0 
    })
  }

  removeAffectionReward(proc: any, index: number) {
    if (proc.affection_reward) {
      proc.affection_reward.splice(index, 1)
    }
  }

  // ========== 彩蛋管理 ==========
  formatEggSummary(egg: any): string {
    let parts: string[] = []
    if (egg.trigger_condition) {
      const tc = egg.trigger_condition.length > 20 ? egg.trigger_condition.substring(0, 20) + '...' : egg.trigger_condition
      parts.push(`触发条件: ${tc}`)
    }
    if (egg.egg_name) parts.push(`触发彩蛋【${egg.egg_name}】`)
    if (egg.award_conf && egg.award_conf.length > 0) {
      egg.award_conf.forEach((a: any) => {
        if (a.type === '成就') parts.push(`奖励成就【${a.reward_description || ''}】`)
        else parts.push(`${a.type}【${a.reward_description || ''}】`)
      })
    }
    return parts.join(', ') || '空彩蛋'
  }

  addEgg(node: GameNode) {
    if (!node.egg) {
      this.$set(node, 'egg', [])
    }
    const eggArray = node.egg as any[];
    eggArray.push({
      egg_name: '',
      trigger_condition: '',
      process: [],
      award_conf: [],
      points_reward: [],
      affection_reward: [],
      _editing: true
    })
  }

  removeEgg(node: GameNode, index: number) {
    if (node.egg) {
      const eggArr = node.egg as any[]
      eggArr.splice(index, 1)
    }
  }

  addProcessGain(egg: any, node: GameNode) {
    if (!egg.process) {
      this.$set(egg, 'process', [])
    }
    // 使用过滤后的进度指标列表
    const validProcesses = this.getProcessList(node)
    const defaultProcessName = validProcesses.length > 0 ? validProcesses[0].name : ''
    egg.process.push({ process_name: defaultProcessName, process_gain: 0 })
  }

  removeProcessGain(egg: any, index: number) {
    if (egg.process) {
      egg.process.splice(index, 1)
    }
  }

  addEggAwardConf(egg: any) {
    if (!egg.award_conf) {
      this.$set(egg, 'award_conf', [])
    }
    egg.award_conf.push({ type: '成就', reward_description: '' })
  }

  removeEggAwardConf(egg: any, index: number) {
    if (egg.award_conf) {
      egg.award_conf.splice(index, 1)
    }
  }

  addEggPointsReward(egg: any) {
    if (!egg.points_reward) {
      this.$set(egg, 'points_reward', [])
    }
    const defaultPointId = this.gamePoints.length > 0 ? this.gamePoints[0].id : 1
    egg.points_reward.push({ points_id: defaultPointId, points_gain: 0 })
  }

  removeEggPointsReward(egg: any, index: number) {
    if (egg.points_reward) {
      egg.points_reward.splice(index, 1)
    }
  }

  addEggAffectionReward(egg: any) {
    if (!egg.affection_reward) {
      this.$set(egg, 'affection_reward', [])
    }
    egg.affection_reward.push({ 
      role_id: this.roles.length > 0 ? this.roles[0].id : undefined, 
      affection_gain: 0 
    })
  }

  removeEggAffectionReward(egg: any, index: number) {
    if (egg.affection_reward) {
      egg.affection_reward.splice(index, 1)
    }
  }

  // ============== 输出示例相关方法 ==============
  addOutputExample(node: GameNode) {
    if (!node.output_examples) {
      this.$set(node, 'output_examples', [])
    }
    const defaultContent = `###旁白
你小心翼翼地推开门，屋内一片昏暗。
###角色名
（对白内容）`
    ;(node.output_examples as any[]).push({
      content: defaultContent
    })
  }

  removeOutputExample(node: GameNode, index: number) {
    if (node.output_examples) {
      node.output_examples.splice(index, 1)
    }
  }

  // ============== 用户消息处理相关方法 ==============
  addUserMessage(node: GameNode) {
    if (!node.user_messages_new) {
      this.$set(node, 'user_messages_new', [])
    }
    node.user_messages_new!.push({
      result: '',
      ai_reply: '',
      progress_name: '',
      progress_value: 0,
      output_examples: []
    })
    // 强制更新视图
    this.$forceUpdate()
  }

  addUserMsgOutputExample(msg: any) {
    if (!msg.output_examples) {
      msg.output_examples = []
    }
    msg.output_examples.push({ content: '' })
  }

  removeUserMsgOutputExample(msg: any, index: number) {
    if (msg.output_examples) {
      msg.output_examples.splice(index, 1)
    }
  }

  removeUserMessage(node: GameNode, index: number) {
    if (node.user_messages_new) {
      node.user_messages_new.splice(index, 1)
    }
  }

  // 获取进度指标列表（用于下拉框）
  getProcessList(node: GameNode): any[] {
    if (!node.process || !Array.isArray(node.process)) {
      return []
    }
    // 过滤掉名称为空的进度指标
    return (node.process as any[]).filter(p => p.name && p.name.trim() !== '')
  }

  // 获取彩蛋列表（用于下拉框）
  getEggList(node: GameNode): any[] {
    if (!node.egg || !Array.isArray(node.egg)) {
      return []
    }
    // 过滤掉名称为空的彩蛋
    return (node.egg as any[]).filter(e => e.egg_name && e.egg_name.trim() !== '')
  }

  // 获取除当前节点外的其他节点列表
  getOtherNodes(currentNode: GameNode): GameNode[] {
    return this.nodes.filter(n => n.id !== currentNode.id)
  }

  // 打开移动对话框
  openMoveDialog(node: GameNode) {
    this.moveDialogNode = node
    this.moveDialogOtherNodes = this.getOtherNodes(node)
    this.selectedMovePosition = null
    this.moveDialogVisible = true
  }

  // 关闭移动对话框
  closeMoveDialog() {
    this.moveDialogNode = null
    this.moveDialogOtherNodes = []
    this.selectedMovePosition = null
  }

  // 确认移动
  confirmMove() {
    if (this.moveDialogNode && this.selectedMovePosition !== null) {
      this.moveNodeToPosition(this.moveDialogNode, this.selectedMovePosition)
      this.moveDialogVisible = false
    }
  }

  // 移动节点到指定位置
  moveNodeToPosition(node: GameNode, position: string | number) {
    if (position === null || position === undefined) {
      return
    }

    // 找到当前节点的索引
    const currentIndex = this.nodes.findIndex(n => n.id === node.id)
    if (currentIndex === -1) return

    // 从数组中移除当前节点
    const [movedNode] = this.nodes.splice(currentIndex, 1)

    // 根据选择的位置插入节点
    if (position === 'top') {
      // 移动到顶部
      this.nodes.unshift(movedNode)
    } else if (position === 'bottom') {
      // 移动到底部
      this.nodes.push(movedNode)
    } else {
      // 移动到指定节点之前
      const targetIndex = position as number
      // 因为已经移除了当前节点，所以需要重新计算目标索引
      const actualTargetIndex = targetIndex >= currentIndex ? targetIndex : targetIndex
      this.nodes.splice(actualTargetIndex, 0, movedNode)
    }

    // 保存新的顺序
    this.onDragEnd()
    
    this.$message.success('节点顺序已调整')
  }

  copyNode(node: GameNode) {
    this.copySourceNode = node
    this.showCopyDialog = true
  }

  async handleCopyConfirm(payload: { chapterId: number; afterNodeId: number; chapterNodes: GameNode[] }) {
    if (!this.copySourceNode) return
    this.showCopyDialog = false

    const src = this.copySourceNode
    const copiedNodeData: GameNode = {
      ...JSON.parse(JSON.stringify(src)),
      id: undefined,
      game_id: this.game.id,
      game_chapter_id: payload.chapterId,
      title: src.title + ' (副本)',
      next_node_id: null,
      ui_show_next_node: null
    }

    // AI 节点清除各结局的 next_node_id
    if (copiedNodeData.node_type === 'ai' && copiedNodeData.current_plot_new?.plot_end) {
      copiedNodeData.current_plot_new.plot_end = (copiedNodeData.current_plot_new.plot_end as any[]).map((e: any) => ({
        ...e, next_node_id: null
      }))
    }

    const nodeData = JSON.parse(JSON.stringify(copiedNodeData))
    delete nodeData.expanded

    try {
      const created: any = await createNode(nodeData)
      const createdId = created.data?.id || created.id

      // 更新"插入到其后"的节点的 next_node_id 指向新节点
      const afterNodeId = payload.afterNodeId
      if (createdId && afterNodeId) {
        const afterNode = payload.chapterNodes.find(n => n.id === afterNodeId)
        if (afterNode && afterNode.id) {
          // 新节点的 next_node_id 设为 afterNode 原来的后续
          const originalNextId = afterNode.next_node_id || null
          if (originalNextId) {
            await updateNode(createdId, {
              next_node_id: originalNextId,
              ui_show_next_node: { next_node_id: originalNextId }
            } as any)
          }

          // afterNode 的 next_node_id 指向新节点
          if (afterNode.node_type === 'ai') {
            const plotNewData = typeof afterNode.current_plot_new === 'string'
              ? JSON.parse(afterNode.current_plot_new)
              : (afterNode.current_plot_new || { plot_start: '', plot_process: '', plot_end: [] })
            const endings = plotNewData.plot_end || []
            let endingsChanged = false
            const updatedEndings = endings.map((e: any) => {
              if (!e.next_node_id) {
                endingsChanged = true
                return { ...e, next_node_id: createdId }
              }
              return e
            })
            const updateData: any = {
              next_node_id: createdId,
              ui_show_next_node: { next_node_id: createdId }
            }
            if (endingsChanged) {
              updateData.current_plot_new = { ...plotNewData, plot_end: updatedEndings }
            }
            await updateNode(afterNode.id, updateData)
          } else {
            await updateNode(afterNode.id, {
              next_node_id: createdId,
              ui_show_next_node: { next_node_id: createdId }
            } as any)
          }
        }
      } else if (createdId && !afterNodeId) {
        // 插入到末尾：找到目标章节最后一个节点
        const targetNodes = payload.chapterNodes
        if (targetNodes.length > 0) {
          const lastNode = targetNodes[targetNodes.length - 1]
          if (lastNode.id && !lastNode.next_node_id) {
            if (lastNode.node_type === 'ai') {
              const plotNewData = typeof lastNode.current_plot_new === 'string'
                ? JSON.parse(lastNode.current_plot_new)
                : (lastNode.current_plot_new || { plot_start: '', plot_process: '', plot_end: [] })
              const endings = plotNewData.plot_end || []
              let endingsChanged = false
              const updatedEndings = endings.map((e: any) => {
                if (!e.next_node_id) {
                  endingsChanged = true
                  return { ...e, next_node_id: createdId }
                }
                return e
              })
              const updateData: any = {
                next_node_id: createdId,
                ui_show_next_node: { next_node_id: createdId }
              }
              if (endingsChanged) {
                updateData.current_plot_new = { ...plotNewData, plot_end: updatedEndings }
              }
              await updateNode(lastNode.id, updateData)
            } else {
              await updateNode(lastNode.id, {
                next_node_id: createdId,
                ui_show_next_node: { next_node_id: createdId }
              } as any)
            }
          }
        }
      }

      this.$message.success('复制成功')
      this.loadData()
    } catch (error: any) {
      console.error('复制节点失败:', error)
      this.$message.error('复制失败: ' + (error.response?.data?.message || error.message))
    }
  }

  // 将dialogues数组转换为文本格式（用于编辑）
  dialoguesToText(dialogues: any[]): string {
    if (!dialogues || !Array.isArray(dialogues) || dialogues.length === 0) {
      return ''
    }
    
    // 找到对应的角色名
    const lines = dialogues.map(d => {
      const role = this.roles.find(r => r.id === d.role_id)
      const roleName = role ? role.name : '未知角色'
      return `${roleName}:${d.text || ''}`
    })
    
    return lines.join('\n')
  }

  // 将文本格式转换为dialogues数组（用于保存）
  textToDialogues(text: string): any[] {
    if (!text || text.trim() === '') {
      return []
    }
    
    const lines = text.split('\n').filter(line => line.trim() !== '')
    const dialogues: any[] = []
    
    for (const line of lines) {
      // 同时支持中文冒号（：）和英文冒号（:）
      let colonIndex = line.indexOf(':')
      let chineseColonIndex = line.indexOf('：')
      
      // 优先使用出现位置靠前的冒号
      if (colonIndex === -1 && chineseColonIndex === -1) {
        // 如果两种冒号都没有，跳过这一行
        continue
      } else if (colonIndex === -1) {
        colonIndex = chineseColonIndex
      } else if (chineseColonIndex !== -1 && chineseColonIndex < colonIndex) {
        colonIndex = chineseColonIndex
      }
      
      const roleName = line.substring(0, colonIndex).trim()
      const dialogueText = line.substring(colonIndex + 1).trim()
      
      // 根据角色名查找role_id
      const role = this.roles.find(r => r.name === roleName)
      if (role) {
        dialogues.push({
          role_id: role.id,
          text: dialogueText
        })
      } else {
        // 如果找不到角色，可以提示用户或创建一个占位符
        console.warn(`未找到角色: ${roleName}`)
      }
    }
    
    return dialogues
  }

  // ============== 进入条件相关方法 ==============
  // 将旧的英文类型值转换为中文类型值（数据兼容性处理）
  migrateEntryConditionTypes(conditions: EntryCondition[]): EntryCondition[] {
    if (!conditions || !Array.isArray(conditions)) return []
    const typeMap: { [key: string]: string } = {
      'currency': '货币',
      'attribute': '属性',
      'affection': '好感度',
      'item': '道具',
      'costume': '服装'
    }
    return conditions.map(cond => {
      if (typeMap[cond.type]) {
        return { ...cond, type: typeMap[cond.type] }
      }
      return cond
    })
  }

  // 根据 type 过滤进入条件字段
  filterEntryConditions(conditions: EntryCondition[]): any[] {
    return conditions.map(cond => {
      const base = { type: cond.type }
      switch (cond.type) {
        case '货币':
          return { ...base, value: cond.value, is_free: cond.is_free }
        case '属性':
          return { ...base, role_id: cond.role_id, attribute_name: cond.attribute_name, attribute_value: cond.attribute_value }
        case '好感度':
          return { ...base, role_id: cond.role_id, affection_value: cond.affection_value }
        case '道具':
          return { ...base, item_name: cond.item_name }
        case '服装':
          return { ...base, role_id: cond.costume_role_id, costume_role_id: cond.costume_role_id, costume_name: cond.costume_name }
        default:
          return base
      }
    })
  }

  addEntryCondition(node: GameNode) {
    if (!node.entry_conditions) {
      this.$set(node, 'entry_conditions', [])
    }
    (node.entry_conditions as EntryCondition[]).push({
      type: '货币',
      // 货币
      value: 0,
      is_free: false,
      // 属性
      attribute_name: '',
      attribute_value: 0,
      // 好感度
      role_id: undefined,
      affection_value: 0,
      // 道具
      item_name: '',
      // 服装
      costume_role_id: undefined,
      costume_name: []
    })
  }

  removeEntryCondition(node: GameNode, index: number) {
    if (node.entry_conditions) {
      (node.entry_conditions as EntryCondition[]).splice(index, 1)
    }
  }

  // 获取角色的服装列表
  getCostumesForRole(roleId: number | undefined): Costume[] {
    if (!roleId) return []
    return this.costumesCache[roleId] || []
  }

  // 当服装条件中角色改变时，加载该角色的服装并清空已选服装
  async onCostumeRoleChange(cond: EntryCondition) {
    cond.costume_name = []
    if (!cond.costume_role_id) return

    // 如果还没有加载过该角色的服装，则加载
    if (!this.costumesCache[cond.costume_role_id]) {
      try {
        const costumes = await getCostumeList(this.game.id, cond.costume_role_id)
        this.$set(this.costumesCache, cond.costume_role_id, costumes)
      } catch (error) {
        console.error('加载服装列表失败:', error)
        this.$set(this.costumesCache, cond.costume_role_id, [])
      }
    }
  }

  // 重新生成：清除已有结果后重新调用
  reExpand() {
    const nid = this.expandDrawerNodeId
    if (!nid) return
    const node = this.nodes.find(n => n.id === nid)
    if (!node) return
    this.$delete(this.expandStates, nid)
    this.expandDrawerVisible = false
    this.$nextTick(() => {
      this.doExpand(node)
    })
  }

  // AI扩写
  async doExpand(node: GameNode) {
    if (!node.id) return
    const nid = node.id

    // 如果该节点已有扩写状态（生成中或已完成），打开抽屉查看
    if (this.expandStates[nid] && (this.expandStates[nid].loading || this.expandStates[nid].text)) {
      this.expandDrawerNodeId = nid
      // 强制重开抽屉以提供视觉反馈
      this.expandDrawerVisible = false
      this.$nextTick(() => {
        this.expandDrawerVisible = true
      })
      return
    }

    // 初始化该节点的状态
    this.$set(this.expandStates, nid, { text: '', loading: true, result: null })
    this.expandDrawerNodeId = nid
    this.expandDrawerVisible = true

    try {
      if (node.node_type === 'fixed') {
        await this.expandFixedNode(node)
      } else {
        await this.expandAINode(node)
      }
    } catch (error: any) {
      console.error('AI扩写失败:', error)
      this.$message.error('AI扩写失败: ' + (error.response?.data?.detail || error.message))
    } finally {
      if (this.expandStates[nid]) {
        this.$set(this.expandStates, nid, { ...this.expandStates[nid], loading: false })
      }
    }
  }

  async expandFixedNode(node: GameNode) {
    const nid = node.id!
    // 解析 dialogue_text 为 PrologueItem 数组
    const plotText: { role: string; content: string }[] = []
    const text = node.dialogue_text || ''
    for (const line of text.split('\n')) {
      const trimmed = line.trim()
      if (!trimmed) continue
      const colonIdx = trimmed.indexOf(':')
      const colonIdx2 = trimmed.indexOf('：')
      const idx = colonIdx >= 0 && colonIdx2 >= 0 ? Math.min(colonIdx, colonIdx2) : Math.max(colonIdx, colonIdx2)
      if (idx > 0) {
        plotText.push({ role: trimmed.slice(0, idx).trim(), content: trimmed.slice(idx + 1).trim() })
      } else {
        plotText.push({ role: '旁白', content: trimmed })
      }
    }

    if (plotText.length === 0) {
      this.$message.warning('固定节点没有剧情内容，无法扩写')
      return
    }

    await expandFixedNodeStream(
      { plot_text: plotText, available_music_types: ['快乐', '轻松', '悲伤', '悬疑', '紧张', '温馨', '史诗'] },
      (chunk) => {
        if (this.expandStates[nid]) {
          this.$set(this.expandStates, nid, { ...this.expandStates[nid], text: this.expandStates[nid].text + chunk })
        }
      },
      (result) => {
        if (this.expandStates[nid]) {
          this.$set(this.expandStates, nid, { ...this.expandStates[nid], result })
        }
      },
      (msg) => { this.$message.error('扩写失败: ' + msg) }
    )
  }

  async expandAINode(node: GameNode) {
    const nid = node.id!
    if (!node.current_plot_new) {
      this.$message.warning('请先填写剧情信息')
      return
    }

    const plot = node.current_plot_new
    // 构建开场白 — 使用 prologue_role_id 确定角色名
    const prologueText = (node.prologue || '').trim()
    const prologue: { role: string; content: string }[] = []
    const prologueRole = node.prologue_role_id
      ? this.roles.find(r => r.id === node.prologue_role_id)
      : null
    const prologueRoleName = prologueRole ? prologueRole.name : '旁白'
    if (prologueText) {
      for (const line of prologueText.split('\n')) {
        const trimmed = line.trim()
        if (!trimmed) continue
        const colonIdx = trimmed.indexOf(':')
        const colonIdx2 = trimmed.indexOf('：')
        const idx = colonIdx >= 0 && colonIdx2 >= 0 ? Math.min(colonIdx, colonIdx2) : Math.max(colonIdx, colonIdx2)
        if (idx > 0) {
          prologue.push({ role: trimmed.slice(0, idx).trim(), content: trimmed.slice(idx + 1).trim() })
        } else {
          prologue.push({ role: prologueRoleName, content: trimmed })
        }
      }
    }
    if (prologue.length === 0) {
      prologue.push({ role: prologueRoleName, content: prologueText || '（开场白待补充）' })
    }

    // 构建结局
    const plotEndings = Array.isArray(plot.plot_end)
      ? (plot.plot_end as any[]).map((e, i) => ({ id: i + 1, description: e.content || e.name || '' }))
      : []

    const roles = this.roles.filter(r => r.id && r.name).map(r => ({
      role_id: r.id,
      role_name: r.name,
      description: [r.bio, r.personality, r.dialogue_style].filter(Boolean).join('。')
    }))

    // 构建好感度列表
    const favorabilities = this.roles.filter(r => r.need_affection).map(r => r.name + '好感度')

    // 构建属性列表（属性已独立为 game_attributes 表；与编辑器属性下拉 attributeNameList 同源，
    // 保证 AI 生成的属性名能匹配上下拉选项，否则扩写出的属性奖励无法填入节点）
    const attributes = this.attributeNameList

    // 收集已有彩蛋名
    // 构建背景摘要：当前节点的story_background 直接作为 previous_plot；
    // 若为空则把前序节点原始数据交给后端 LLM 生成高质量背景摘要
    const previousPlot = (node.story_background || '').trim()
    let previousNodes: any[] | undefined
    if (!previousPlot) {
      const currentIdx = this.nodes.findIndex(n => n.id === node.id)
      if (currentIdx > 0) {
        previousNodes = this.nodes.slice(0, currentIdx)
      }
    }

    const existingEggs: string[] = []
    const existingEvents: string[] = []
    const collectEvents = (awardConf: any[]) => {
      for (const a of awardConf) {
        if (a.type === '事件' && a.reward_description) existingEvents.push(a.reward_description)
      }
    }
    for (const n of this.nodes) {
      if (n.egg && Array.isArray(n.egg)) {
        for (const e of n.egg as any[]) {
          if (e.egg_name) existingEggs.push(e.egg_name)
          if (e.award_conf && Array.isArray(e.award_conf)) collectEvents(e.award_conf)
        }
      }
      if (n.process && Array.isArray(n.process)) {
        for (const p of n.process as any[]) {
          if (p.award_conf && Array.isArray(p.award_conf)) collectEvents(p.award_conf)
        }
      }
    }

    await expandAINodeStream(
      {
        previous_plot: previousPlot,
        previous_nodes: previousNodes,
        target_node_id: node.id,
        prologue,
        task: node.node_task || '',
        plot_start: plot.plot_start || '',
        plot_process: plot.plot_process || '',
        plot_endings: plotEndings,
        image_requirements: node.image_prompt || '',
        user_role: this.roles.find(r => r.role_type === 'USER')?.name || '用户',
        roles,
        attributes,
        favorabilities,
        existing_events: existingEvents,
        existing_easter_eggs: existingEggs,
        available_music_types: ['快乐', '轻松', '悲伤', '悬疑', '紧张', '温馨', '史诗'],
      },
      (chunk) => {
        if (this.expandStates[nid]) {
          this.$set(this.expandStates, nid, { ...this.expandStates[nid], text: this.expandStates[nid].text + chunk })
        }
      },
      (result) => {
        if (this.expandStates[nid]) {
          this.$set(this.expandStates, nid, { ...this.expandStates[nid], result })
        }
      },
      (msg) => { this.$message.error('扩写失败: ' + msg) }
    )
  }

  copyExpandText() {
    const nid = this.expandDrawerNodeId
    const text = nid && this.expandStates[nid] ? this.expandStates[nid].text : ''
    if (!text) return
    navigator.clipboard.writeText(text).then(() => {
      this.$message.success('已复制到剪贴板')
    }).catch(() => {
      // fallback
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      this.$message.success('已复制到剪贴板')
    })
  }

  applyExpandResultToNode() {
    const nid = this.expandDrawerNodeId
    if (!nid) return
    const state = this.expandStates[nid]
    if (!state || !state.result) {
      this.$message.warning('没有可应用的结果')
      return
    }
    const node = this.nodes.find(n => n.id === nid)
    if (!node) {
      this.$message.error('找不到节点')
      return
    }

    const result = state.result as AINodeResponse

    // 映射 API 结果到节点字段
    // 1. 标题
    if (result.node_title) {
      this.$set(node, 'title', result.node_title)
    }

    // 2. 图片提示词
    if (result.image_prompt) {
      this.$set(node, 'image_prompt', result.image_prompt)
    }

    // 2.5 开场白 - 将结构化 prologue 转回文本（剥离角色名前缀）
    if (result.prologue && Array.isArray(result.prologue) && result.prologue.length > 0) {
      const prologueText = result.prologue
        .map((p: any) => {
          let content = p.content || ''
          // 剥离 content 中可能残留的"角色名："前缀
          if (p.role && content) {
            const prefixes = [`${p.role}：`, `${p.role}:`]
            for (const prefix of prefixes) {
              if (content.startsWith(prefix)) {
                content = content.slice(prefix.length).trim()
                break
              }
            }
          }
          return content
        })
        .filter(Boolean)
        .join('\n')
      this.$set(node, 'prologue', prologueText)
      // 同时更新开场白角色（取第一个角色）
      const firstRole = result.prologue[0].role
      if (firstRole) {
        const matchedRole = this.roles.find(r => r.name === firstRole)
        if (matchedRole) {
          this.$set(node, 'prologue_role_id', matchedRole.id)
        }
      }
    }

    // 3. 故事背景
    if (result.background) {
      this.$set(node, 'story_background', result.background)
    }

    // 3.5 参与角色 - 用 scene_roles（后端通过剧情文本匹配得到的角色名）映射 role_ids
    if (result.scene_roles && Array.isArray(result.scene_roles) && result.scene_roles.length > 0) {
      const sceneRoleIds = result.scene_roles
        .map((name: string) => this.roles.find(r => r.name === name)?.id)
        .filter((id): id is number => id !== undefined)
      if (sceneRoleIds.length > 0) {
        this.$set(node, 'role_ids', sceneRoleIds)
      }
    }

    // 3.6 回合数限制 - 默认5
    if (!node.limit_turn) {
      this.$set(node, 'limit_turn', 5)
    }

    // 4. 用户消息分支 - 映射到 user_messages_new
    if (result.user_messages && Array.isArray(result.user_messages)) {
      const userMessagesNew = result.user_messages.map(msg => ({
        result: msg.trigger || '',
        ai_reply: msg.ai_reply || '',
        progress_name: msg.progress_name || '',
        progress_value: msg.progress_gain || 0,
        output_examples: (msg.examples || []).map(ex => ({ content: ex }))
      }))
      this.$set(node, 'user_messages_new', userMessagesNew)
    }

    // 5. 彩蛋 - 映射到 egg (v2.0 格式)
    if (result.easter_eggs && Array.isArray(result.easter_eggs)) {
      const eggs = result.easter_eggs.map(egg => {
        // 成就奖励：AI 生成的成就名（缺省回退彩蛋名），填入彩蛋的 award_conf
        const achievement = (egg.achievement || egg.name || '').trim()
        return {
          egg_name: egg.name || '',
          trigger_condition: egg.trigger || '',
          process: [],
          award_conf: achievement ? [{ type: '成就', reward_description: achievement }] : [],
          points_reward: [],
          affection_reward: []
        }
      })
      this.$set(node, 'egg', eggs)
    }

    // 5.5 剧情（current_plot → current_plot_new）
    if (result.current_plot) {
      if (!node.current_plot_new) {
        this.$set(node, 'current_plot_new', { plot_start: '', plot_process: '', plot_end: [] })
      }
      if (result.current_plot.start) {
        this.$set(node.current_plot_new!, 'plot_start', result.current_plot.start)
      }
      if (result.current_plot.process) {
        this.$set(node.current_plot_new!, 'plot_process', result.current_plot.process)
      }
      if (result.current_plot.plot_endings && Array.isArray(result.current_plot.plot_endings)) {
        const plotEnd = result.current_plot.plot_endings.map((e: any) => ({
          name: e.name || '',
          content: e.content || e.description || '',
          output_examples: [],
          next_node_id: null
        }))
        // 保留已有结局的 next_node_id
        if (node.current_plot_new!.plot_end && Array.isArray(node.current_plot_new!.plot_end)) {
          for (let i = 0; i < plotEnd.length && i < node.current_plot_new!.plot_end.length; i++) {
            plotEnd[i].next_node_id = (node.current_plot_new!.plot_end as any[])[i].next_node_id || null
          }
        }
        this.$set(node.current_plot_new!, 'plot_end', plotEnd)
      }
    }

    // 5.6 任务描述
    if (result.task) {
      this.$set(node, 'node_task', result.task)
    }

    // 6. 进度指标 - 映射到 process (v2.0 格式)
    if (result.progress_indicators && Array.isArray(result.progress_indicators)) {
      const processes = result.progress_indicators.map(indicator => {
        const proc: any = {
          name: indicator.name || '',
          start_process: indicator.min_value || 0,
          end_process: indicator.max_value || 100,
          ending_name: indicator.linked_ending || '',
          next_node_id: null
        }
        // 奖励映射 - 只在有奖励时才添加
        if (indicator.rewards) {
          const awardConf: any[] = []
          
          if (indicator.rewards.event) {
            awardConf.push({
              type: '事件',
              reward_description: indicator.rewards.event
            })
          }
          if (indicator.rewards.attributes && Array.isArray(indicator.rewards.attributes) && indicator.rewards.attributes.length > 0) {
            for (const attr of indicator.rewards.attributes) {
              if (attr.name && attr.value !== undefined) {
                awardConf.push({
                  type: '属性',
                  attribute_name: attr.name,
                  attribute_value: attr.value
                })
              }
            }
          }
          if (indicator.rewards.favorabilities && Array.isArray(indicator.rewards.favorabilities) && indicator.rewards.favorabilities.length > 0) {
            for (const fav of indicator.rewards.favorabilities) {
              const roleId = this.findRoleIdByName(fav.name)
              if (roleId && fav.value !== undefined) {
                awardConf.push({
                  type: '好感度',
                  role_id: roleId,
                  affection_value: fav.value
                })
              }
            }
          }
          
          if (awardConf.length > 0) {
            proc.award_conf = awardConf
          }
          proc.affection_reward = []
          proc.points_reward = []
        }
        return proc
      })
      this.$set(node, 'process', processes)

      // 将 ending_example 映射到对应结尾的 output_examples
      if (node.current_plot_new && Array.isArray(node.current_plot_new.plot_end)) {
        for (const indicator of result.progress_indicators) {
          if (indicator.ending_example && indicator.linked_ending) {
            // 根据 linked_ending 找到对应的结尾（如 "结尾1" -> 第一个结尾）
            const endingIdx = this.parseEndingIndex(indicator.linked_ending)
            if (endingIdx >= 0 && endingIdx < node.current_plot_new.plot_end.length) {
              const ending = node.current_plot_new.plot_end[endingIdx]
              this.$set(ending, 'output_examples', [{ content: indicator.ending_example }])
            }
          }
        }
      }
    }

    // 关闭抽屉
    this.expandDrawerVisible = false

    // 自动保存
    this.$nextTick(() => {
      this.updateNodeData(node)
    })
  }

  findRoleIdByName(name: string): number | undefined {
    const cleanName = name.replace(/好感度$/, '').trim()
    const role = this.roles.find(r => r.name === name || r.name === cleanName)
    return role?.id
  }

  parseEndingIndex(linkedEnding: string): number {
    const match = linkedEnding.match(/结局(\d+)/)
    if (match) {
      return parseInt(match[1], 10) - 1
    }
    return -1
  }

  // ========== AI局部调整 ==========

  findIndicatorIndexByEnding(node: GameNode, ending: any): number | null {
    if (!node.process || !Array.isArray(node.process)) return null
    const endingName = ending.name || ''
    const idx = (node.process as any[]).findIndex((p: any) => p.ending_name === endingName)
    return idx >= 0 ? idx : null
  }

  buildRefineTaskKey(
    nodeId: number,
    section: RefineSectionType,
    branchIndex: number | null = null,
    indicatorIndex: number | null = null,
    eggIndex: number | null = null
  ) {
    return [
      nodeId,
      section,
      branchIndex ?? 'x',
      indicatorIndex ?? 'x',
      eggIndex ?? 'x'
    ].join(':')
  }

  ensureRefineState(
    key: string,
    meta: {
      nodeId: number
      section: RefineSectionType
      label: string
      branchIndex: number | null
      indicatorIndex: number | null
      eggIndex: number | null
    }
  ) {
    const existing = this.refineStates[key]
    if (existing) {
      this.$set(this.refineStates, key, {
        ...existing,
        ...meta,
      })
      return this.refineStates[key]
    }

    const created: RefineTaskState = {
      key,
      nodeId: meta.nodeId,
      section: meta.section,
      label: meta.label,
      branchIndex: meta.branchIndex,
      indicatorIndex: meta.indicatorIndex,
      eggIndex: meta.eggIndex,
      suggestion: '',
      streamText: '',
      result: '',
      loading: false,
      error: '',
    }
    this.$set(this.refineStates, key, created)
    return created
  }

  updateRefineState(key: string, patch: Partial<RefineTaskState>) {
    const current = this.refineStates[key]
    if (!current) return
    this.$set(this.refineStates, key, {
      ...current,
      ...patch,
    })
  }

  getNodeSceneRoles(node: GameNode): string[] {
    const nodeId = Number(node.id || 0)
    const expandResult = nodeId ? this.expandStates[nodeId]?.result as AINodeResponse | undefined : undefined
    if (expandResult?.scene_roles && expandResult.scene_roles.length > 0) {
      return Array.from(new Set(expandResult.scene_roles.filter(Boolean)))
    }

    const plot = node.current_plot_new || { plot_start: '', plot_process: '', plot_end: [] as any[] }
    const textParts: string[] = [
      node.prologue || '',
      node.node_task || '',
      (plot as any).plot_start || '',
      (plot as any).plot_process || '',
    ]

    if (Array.isArray(plot.plot_end)) {
      for (const ending of plot.plot_end as any[]) {
        textParts.push(ending?.name || '')
        textParts.push(ending?.content || '')
        if (Array.isArray(ending?.output_examples)) {
          textParts.push(...ending.output_examples.map((ex: any) => ex?.content || ''))
        }
      }
    }

    if (Array.isArray(node.user_messages_new)) {
      for (const msg of node.user_messages_new as any[]) {
        textParts.push(msg?.result || '')
        textParts.push(msg?.ai_reply || '')
        if (Array.isArray(msg?.output_examples)) {
          textParts.push(...msg.output_examples.map((ex: any) => ex?.content || ''))
        }
      }
    }

    if (Array.isArray(node.egg)) {
      for (const egg of node.egg as any[]) {
        textParts.push(egg?.egg_name || '')
        textParts.push(egg?.trigger_condition || '')
      }
    }

    const textSource = textParts.join('\n')
    const userRole = this.roles.find(r => (r as any).role_type === 'USER')?.name || '用户'
    const names: string[] = []
    const pushName = (name: string) => {
      const normalized = (name || '').trim()
      if (normalized && !names.includes(normalized)) {
        names.push(normalized)
      }
    }

    for (const role of this.roles) {
      const roleName = ((role as any).name || '').trim()
      if (!roleName) continue
      if (roleName === userRole || textSource.includes(roleName)) {
        pushName(roleName)
      }
    }

    pushName(userRole)
    return names
  }

  openRefineDialog(
    node: GameNode,
    section: RefineSectionType,
    label: string,
    branchIndex: number | null = null,
    indicatorIndex: number | null = null,
    eggIndex: number | null = null
  ) {
    const nodeId = Number(node.id || 0)
    const key = this.buildRefineTaskKey(nodeId, section, branchIndex, indicatorIndex, eggIndex)
    const state = this.ensureRefineState(key, {
      nodeId,
      section,
      label,
      branchIndex,
      indicatorIndex,
      eggIndex,
    })

    this.refineDialogKey = key
    this.refineNodeId = node.id || null
    this.refineSection = section
    this.refineSectionLabel = label
    this.refineBranchIndex = branchIndex
    this.refineIndicatorIndex = indicatorIndex
    this.refineEggIndex = eggIndex
    this.refineSuggestion = state.suggestion || ''
    this.refineDialogVisible = true
  }

  buildRefineNodeData(node: GameNode) {
    const plot = node.current_plot_new || { plot_start: '', plot_process: '', plot_end: [] as any[] }
    const plotEndings = Array.isArray(plot.plot_end)
      ? (plot.plot_end as any[]).map((e: any, i: number) => ({ id: i + 1, description: e.content || e.name || '' }))
      : []
    const currentIdx = this.nodes.findIndex(n => n.id === node.id)
    const previousNodes = currentIdx >= 0
      ? this.nodes.slice(0, currentIdx)
      : this.nodes.filter(n => n.id !== node.id)

    const roles = this.roles.map(r => ({
      role_id: (r as any).id,
      role_name: (r as any).name,
      description: [(r as any).description, (r as any).bio, (r as any).personality, (r as any).dialogue_style]
        .filter(Boolean)
        .join('。')
    }))

    const prologueText = node.prologue || ''
    const refineRoleName = node.prologue_role_id
      ? (this.roles.find(r => r.id === node.prologue_role_id)?.name || '旁白')
      : '旁白'
    const prologue: Array<{role: string; content: string}> = []
    for (const line of prologueText.split('\n')) {
      const trimmed = line.trim()
      if (!trimmed) continue
      const idx = trimmed.indexOf('：') >= 0 ? trimmed.indexOf('：') : trimmed.indexOf(':')
      if (idx > 0) {
        prologue.push({ role: trimmed.slice(0, idx).trim(), content: trimmed.slice(idx + 1).trim() })
      } else {
        prologue.push({ role: refineRoleName, content: trimmed })
      }
    }

    const expandResult = node.id ? (this.expandStates[node.id]?.result as AINodeResponse | undefined) : undefined

    const userMessages = (node.user_messages_new || []).map((msg: any) => ({
      trigger: msg.result || '',
      ai_reply: msg.ai_reply || '',
      progress_name: msg.progress_name || '',
      progress_gain: msg.progress_value || 0,
      examples: (msg.output_examples || []).map((ex: any) => ex.content || '')
    }))

    const easterEggs = (node.egg || []).map((egg: any) => ({
      name: egg.egg_name || '',
      trigger: egg.trigger_condition || ''
    }))

    const progressIndicators = (node.process || []).map((proc: any) => {
      const ending = Array.isArray(plot.plot_end)
        ? (plot.plot_end as any[]).find((e: any) => e.name === proc.ending_name)
        : null
      return {
        name: proc.name || '',
        min_value: proc.start_process || 0,
        max_value: proc.end_process || 100,
        linked_ending: proc.ending_name || '',
        ending_example: ending?.output_examples?.[0]?.content || ''
      }
    })

    return {
      image_prompt: node.image_prompt || '',
      background: node.story_background || '',
      prologue,
      character_status: expandResult?.character_status || [],
      task: node.node_task || '',
      user_messages: userMessages,
      easter_eggs: easterEggs,
      progress_indicators: progressIndicators,
      plot_start: (plot as any).plot_start || '',
      plot_process: (plot as any).plot_process || '',
      plot_endings: plotEndings,
      previous_nodes: previousNodes,
      target_node_id: node.id,
      user_role: this.roles.find(r => (r as any).role_type === 'USER')?.name || '用户',
      roles,
      scene_roles: this.getNodeSceneRoles(node),
    }
  }

  async doRefine() {
    if (!this.refineNodeId || !this.refineSuggestion.trim() || !this.refineDialogKey) return
    const node = this.nodes.find(n => n.id === this.refineNodeId)
    if (!node) return

    const key = this.refineDialogKey
    this.ensureRefineState(key, {
      nodeId: Number(this.refineNodeId),
      section: this.refineSection,
      label: this.refineSectionLabel,
      branchIndex: this.refineBranchIndex,
      indicatorIndex: this.refineIndicatorIndex,
      eggIndex: this.refineEggIndex,
    })

    const suggestion = this.refineSuggestion.trim()
    this.updateRefineState(key, {
      suggestion,
      streamText: '',
      result: '',
      loading: true,
      error: '',
    })

    const nodeData = this.buildRefineNodeData(node)

    try {
      await refineSectionStream(
        {
          section: this.refineSection,
          user_suggestion: suggestion,
          branch_index: this.refineBranchIndex,
          indicator_index: this.refineIndicatorIndex,
          egg_index: this.refineEggIndex,
          ...nodeData,
        },
        (chunk: string) => {
          const current = this.refineStates[key]
          if (!current) return
          this.updateRefineState(key, { streamText: current.streamText + chunk })
        },
        (result: RefineSectionResponse) => {
          this.updateRefineState(key, {
            result: result.refined_content,
            loading: false,
            error: '',
          })
        },
        (msg: string) => {
          this.updateRefineState(key, {
            loading: false,
            error: msg,
          })
          this.$message.error('调整失败: ' + msg)
        }
      )
    } catch (error: any) {
      const message = error.response?.data?.detail || error.message
      this.updateRefineState(key, {
        loading: false,
        error: message,
      })
      this.$message.error('调整失败: ' + message)
    }
  }

  parseCurrentPlotRefineContent(content: string) {
    const normalized = (content || '').replace(/\r/g, '').trim()
    if (!normalized) {
      return { plotStart: '', plotProcess: '', plotEndings: [] as Array<{ index: number; content: string }> }
    }

    const plotStartLines: string[] = []
    const plotProcessLines: string[] = []
    const plotEndingLines = new Map<number, string[]>()
    let activeTarget: { type: 'start' | 'process' | 'ending'; index?: number } | null = null

    for (const rawLine of normalized.split('\n')) {
      const line = rawLine.trim()
      if (!line) continue

      let match = line.match(/^\u5267\u60c5\u5f00\u7aef[:\uFF1A]\s*(.*)$/)
      if (match) {
        activeTarget = { type: 'start' }
        if (match[1]) plotStartLines.push(match[1])
        continue
      }

      match = line.match(/^\u5267\u60c5\u8fc7\u7a0b[:\uFF1A]\s*(.*)$/)
      if (match) {
        activeTarget = { type: 'process' }
        if (match[1]) plotProcessLines.push(match[1])
        continue
      }

      match = line.match(/^\u5267\u60c5\u7ed3\u5c3e(\d+)[:\uFF1A]\s*(.*)$/)
      if (match) {
        const endingIndex = Number(match[1])
        const currentLines = plotEndingLines.get(endingIndex) || []
        if (match[2]) currentLines.push(match[2])
        plotEndingLines.set(endingIndex, currentLines)
        activeTarget = { type: 'ending', index: endingIndex }
        continue
      }

      if (activeTarget?.type === 'start') {
        plotStartLines.push(line)
      } else if (activeTarget?.type === 'process') {
        plotProcessLines.push(line)
      } else if (activeTarget?.type === 'ending' && activeTarget.index !== undefined) {
        const currentLines = plotEndingLines.get(activeTarget.index) || []
        currentLines.push(line)
        plotEndingLines.set(activeTarget.index, currentLines)
      } else {
        plotProcessLines.push(line)
      }
    }

    const plotEndings = Array.from(plotEndingLines.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([index, lines]) => ({
        index,
        content: lines.join('\n').trim()
      }))
      .filter(item => item.content)

    return {
      plotStart: plotStartLines.join('\n').trim(),
      plotProcess: plotProcessLines.join('\n').trim(),
      plotEndings,
    }
  }

  applyRefineResult() {
    const state = this.activeRefineState
    if (!state || !state.result || !this.refineNodeId) return
    const node = this.nodes.find(n => n.id === this.refineNodeId)
    if (!node) return

    const content = state.result
    const section = this.refineSection

    if (section === 'image_prompt') {
      this.$set(node, 'image_prompt', content)
    } else if (section === 'background') {
      this.$set(node, 'story_background', content)
    } else if (section === 'prologue') {
      this.$set(node, 'prologue', content)
    } else if (section === 'current_plot') {
      if (!node.current_plot_new) {
        this.$set(node, 'current_plot_new', { plot_start: '', plot_process: '', plot_end: [] })
      }
      const parsed = this.parseCurrentPlotRefineContent(content)
      this.$set(node.current_plot_new!, 'plot_start', parsed.plotStart)
      this.$set(node.current_plot_new!, 'plot_process', parsed.plotProcess)
      if (!Array.isArray(node.current_plot_new!.plot_end)) {
        this.$set(node.current_plot_new!, 'plot_end', [])
      }
      parsed.plotEndings.forEach(({ index, content: endingContent }) => {
        const endingIdx = index - 1
        const existingEnding = (node.current_plot_new!.plot_end as any[])[endingIdx]
        if (existingEnding) {
          this.$set(existingEnding, 'content', endingContent)
        } else {
          (node.current_plot_new!.plot_end as any[]).splice(endingIdx, 0, {
            name: `结局${index}`,
            content: endingContent,
            output_examples: []
          })
        }
      })
    } else if (section === 'user_message_trigger' && this.refineBranchIndex !== null) {
      const msgs = node.user_messages_new || []
      if (msgs[this.refineBranchIndex]) {
        this.$set(msgs[this.refineBranchIndex], 'result', content)
      }
    } else if (section === 'user_message_ai_reply' && this.refineBranchIndex !== null) {
      const msgs = node.user_messages_new || []
      if (msgs[this.refineBranchIndex]) {
        this.$set(msgs[this.refineBranchIndex], 'ai_reply', content)
      }
    } else if (section === 'user_message_example' && this.refineBranchIndex !== null) {
      const msgs = node.user_messages_new || []
      if (msgs[this.refineBranchIndex]) {
        this.$set(msgs[this.refineBranchIndex], 'output_examples', [{ content }])
      }
    } else if (section === 'easter_egg_name' && this.refineEggIndex !== null) {
      const eggs = node.egg || []
      if (eggs[this.refineEggIndex]) {
        this.$set(eggs[this.refineEggIndex] as any, 'egg_name', content)
      }
    } else if (section === 'easter_egg_trigger' && this.refineEggIndex !== null) {
      const eggs = node.egg || []
      if (eggs[this.refineEggIndex]) {
        this.$set(eggs[this.refineEggIndex] as any, 'trigger_condition', content)
      }
    } else if (section === 'ending_example' && this.refineIndicatorIndex !== null) {
      const plot = node.current_plot_new
      if (plot && Array.isArray(plot.plot_end)) {
        const proc = (node.process || [])[this.refineIndicatorIndex] as any
        if (proc?.ending_name) {
          const endingIdx = (plot.plot_end as any[]).findIndex((e: any) => e.name === proc.ending_name)
          if (endingIdx >= 0) {
            this.$set(plot.plot_end[endingIdx] as any, 'output_examples', [{ content }])
          }
        }
      }
    }

    this.refineDialogVisible = false
    this.$message.success('已应用调整结果')

    this.$nextTick(() => {
      this.updateNodeData(node)
    })
  }

  formatExpandResult(result: AINodeResponse | FixedNodeResponse): { label: string; content: string }[] {
    const sections: { label: string; content: string }[] = []
    if (!result) return sections

    // 固定节点结果：nodes 数组
    if ('nodes' in result && Array.isArray((result as FixedNodeResponse).nodes)) {
      const fixedResult = result as FixedNodeResponse
      for (const node of fixedResult.nodes) {
        sections.push({ label: `--- 固定节点 ${node.order} ---`, content: '' })
        if (node.plot_text && node.plot_text.length > 0) {
          sections.push({
            label: '剧情文本',
            content: node.plot_text.map(p => `${p.role}：${p.content}`).join('\n')
          })
        }
        if (node.image_prompt) {
          sections.push({ label: '图片提示词', content: node.image_prompt })
        }
        if (node.music_type) {
          sections.push({ label: '音乐类型', content: node.music_type + (node.is_new_music_type ? '（新）' : '') })
        }
      }
      return sections
    }

    const aiResult = result as AINodeResponse
    if (aiResult.node_title) {
      sections.push({ label: '标题', content: aiResult.node_title })
    }
    if (aiResult.background) {
      sections.push({ label: '故事背景', content: aiResult.background })
    }
    if (aiResult.image_prompt) {
      sections.push({ label: '图片提示词', content: aiResult.image_prompt })
    }
    if (aiResult.music_type) {
      sections.push({ label: '音乐类型', content: aiResult.music_type })
    }
    if (aiResult.scene_roles && aiResult.scene_roles.length > 0) {
      sections.push({ label: '场景角色', content: aiResult.scene_roles.join('、') })
    }
    if (aiResult.prologue && aiResult.prologue.length > 0) {
      sections.push({
        label: '开场白内容',
        content: aiResult.prologue.map(p => `${p.role}：${p.content}`).join('\n')
      })
    }
    if (aiResult.task) {
      sections.push({ label: '攻略方式', content: aiResult.task })
    }
    if (aiResult.current_plot) {
      const cp = aiResult.current_plot
      const parts: string[] = []
      if (cp.start) parts.push(`剧情开端：${cp.start}`)
      if (cp.process) parts.push(`剧情过程：${cp.process}`)
      if (cp.plot_endings && cp.plot_endings.length > 0) {
        for (const e of cp.plot_endings) {
          parts.push(`结局${e.id}：${e.description}`)
        }
      }
      if (parts.length > 0) {
        sections.push({ label: '当前剧情', content: parts.join('\n') })
      }
    }
    if (aiResult.user_messages && aiResult.user_messages.length > 0) {
      const lines = aiResult.user_messages.map((msg, i) => {
        const parts = [`消息${i + 1}：`]
        parts.push(`  如果用户发的消息：${msg.trigger}`)
        parts.push(`  则（AI回复方式）：${msg.ai_reply}`)
        if (msg.progress_name) parts.push(`  进度指标名称：${msg.progress_name}`)
        if (msg.progress_gain) parts.push(`  实化数值：${msg.progress_gain}`)
        if (msg.examples && msg.examples.length > 0) {
          parts.push(`  输出示例：\n${msg.examples.map(ex => `    ${ex}`).join('\n')}`)
        }
        return parts.join('\n')
      })
      sections.push({ label: '用户消息列表', content: lines.join('\n\n') })
    }
    if (aiResult.progress_indicators && aiResult.progress_indicators.length > 0) {
      const lines = aiResult.progress_indicators.map((pi, i) => {
        const parts = [`进度指标${i + 1}：`]
        parts.push(`  指标名称：${pi.name}`)
        parts.push(`  起始值：${pi.min_value}`)
        parts.push(`  目标值：${pi.max_value}`)
        if (pi.linked_ending) parts.push(`  绑定结局：${pi.linked_ending}`)
        if (pi.ending_example) parts.push(`  输出示例：${pi.ending_example}`)
        if (pi.rewards) {
          if (pi.rewards.event) parts.push(`  事件奖励：${pi.rewards.event}`)
          if (pi.rewards.attributes && pi.rewards.attributes.length > 0) {
            parts.push(`  属性奖励：${pi.rewards.attributes.map(a => `${a.name} ${a.value > 0 ? '+' : ''}${a.value}`).join('、')}`)
          }
          if (pi.rewards.favorabilities && pi.rewards.favorabilities.length > 0) {
            parts.push(`  好感度奖励：${pi.rewards.favorabilities.map(f => `${f.name} ${f.value > 0 ? '+' : ''}${f.value}`).join('、')}`)
          }
        }
        return parts.join('\n')
      })
      sections.push({ label: '进度指标列表', content: lines.join('\n\n') })
    }
    if (aiResult.easter_eggs && aiResult.easter_eggs.length > 0) {
      const lines = aiResult.easter_eggs.map((egg, i) => {
        return `彩蛋${i + 1}：\n  彩蛋名称：${egg.name}\n  触发条件：${egg.trigger}`
      })
      sections.push({ label: '彩蛋列表', content: lines.join('\n\n') })
    }
    return sections
  }

  // 预加载所有服装条件中涉及的角色服装列表
  async preloadCostumesForEntryConditions() {
    const roleIds = new Set<number>()

    // 收集所有需要加载服装的角色ID
    for (const node of this.nodes) {
      if (node.entry_conditions && Array.isArray(node.entry_conditions)) {
        for (const cond of node.entry_conditions) {
          if (cond.type === '服装' && cond.costume_role_id) {
            roleIds.add(cond.costume_role_id)
          }
        }
      }
    }

    // 并行加载所有角色的服装列表
    const loadPromises = Array.from(roleIds).map(async (roleId) => {
      if (!this.costumesCache[roleId]) {
        try {
          const costumes = await getCostumeList(this.game.id, roleId)
          this.$set(this.costumesCache, roleId, costumes)
        } catch (error) {
          console.error(`加载角色${roleId}的服装列表失败:`, error)
          this.$set(this.costumesCache, roleId, [])
        }
      }
    })

    await Promise.all(loadPromises)
  }
}
</script>

<style scoped>
.node-wrapper-v2 {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

/* 游戏概要卡片 */
.summary-card {
  margin-bottom: 32px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.summary-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

/* 故事概要行 */
.summary-row {
  display: flex;
  align-items: stretch;
}

/* 左侧表单 */
.summary-form {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.summary-form .el-form-item {
  margin-bottom: 18px;
}

.summary-form .summary-item {
  margin-bottom: 0;
}

/* 让"故事概要"标签与输入框在同一行顶部对齐 */
.summary-form .summary-item >>> .el-form-item__label {
  line-height: 40px;
  padding-top: 0;
}

.summary-form .summary-item >>> .el-form-item__content {
  line-height: 40px;
}

.summary-textarea >>> .el-textarea__inner {
  font-size: 14px;
  line-height: 1.6;
  min-height: 156px;
  resize: vertical;
}

/* 右侧封面列 */
.cover-col {
  display: flex;
  flex-direction: column;
}

/* 封面区域 */
.cover-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.cover-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 10px;
  line-height: 1;
}

.cover-upload-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.cover-uploader {
  flex: 1;
  display: flex;
}

.cover-uploader >>> .el-upload {
  flex: 1;
  display: flex;
}

.cover-preview {
  position: relative;
  height: 100%;
  aspect-ratio: 1 / 2;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cover-preview:hover {
  border-color: #6366f1;
}

.cover-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

/* 封面占位符 */
.cover-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #f9fafb;
  color: #9ca3af;
  transition: all 0.3s ease;
}

.cover-placeholder i {
  font-size: 40px;
  margin-bottom: 10px;
}

.placeholder-text {
  font-size: 13px;
  color: #6b7280;
}

.cover-placeholder:hover {
  background: #f3f4f6;
  color: #6366f1;
}

.cover-placeholder:hover .placeholder-text {
  color: #6366f1;
}

/* 封面遮罩层 */
.cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
}

.cover-preview:hover .cover-overlay {
  opacity: 1;
}

.cover-overlay i {
  font-size: 32px;
  margin-bottom: 8px;
}

.overlay-text {
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  padding: 0 16px;
}

/* 分节标题 */
.section-header {
  margin: 32px 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #6366f1;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

/* 节点块 */
.node-block {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  padding: 0;
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.3s ease;
}

.node-block:hover {
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
  border-color: #c7d2fe;
}

/* 节点头部 */
.node-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-bottom: 1px solid #e5e7eb;
}

.node-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.drag-handle {
  cursor: grab;
  padding: 6px 10px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;
}

.drag-handle:hover {
  background: #6366f1;
  border-color: #6366f1;
  color: #ffffff;
}

.drag-handle:active {
  cursor: grabbing;
}

.node-title-text {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.node-header-right {
  display: flex;
  gap: 8px;
}

/* 节点内容区 */
.node-block .el-form {
  padding: 24px;
}

/* 固定节点布局 */
.fixed-node-layout {
  display: flex;
  gap: 24px;
  padding: 24px;
  align-items: stretch;
}

.fixed-node-left {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.fixed-node-left .el-form {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.fixed-node-left .el-form-item:last-of-type {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.fixed-node-left .el-form-item:last-of-type >>> .el-form-item__content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.fixed-node-textarea {
  flex: 1;
}

.fixed-node-textarea >>> .el-textarea__inner {
  height: 100% !important;
  min-height: 200px;
}

.fixed-node-right {
  width: 280px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* 背景图区域 */
.media-section.bg-section {
  flex-shrink: 0;
}

.bg-upload-wrapper {
  width: 100%;
}

.bg-upload-wrapper >>> .el-upload {
  width: 100%;
  display: block;
}

.bg-preview-container {
  width: 100%;
}

.bg-preview {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 2;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.bg-preview:hover {
  border-color: #6366f1;
}

.bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.bg-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #f9fafb;
  color: #9ca3af;
  font-size: 14px;
}

.bg-placeholder i {
  font-size: 32px;
  margin-bottom: 8px;
}

.bg-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
}

.bg-preview:hover .bg-overlay {
  opacity: 1;
}

.bg-overlay i {
  font-size: 28px;
  margin-bottom: 8px;
}

.overlay-text {
  font-size: 13px;
  font-weight: 500;
}

.bg-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.bg-filename:hover {
  background: rgba(0, 0, 0, 0.7);
  border-color: #6366f1;
}

.bg-filename:hover .label,
.bg-filename:hover .filename {
  color: white;
}

.bg-filename .label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  transition: color 0.3s ease;
}

.bg-filename .filename {
  font-size: 12px;
  color: #374151;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
}

/* 音乐区域 */
.media-section.music-section {
  flex-shrink: 0;
}

.music-player {
  margin-bottom: 8px;
}

.music-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #9ca3af;
  font-size: 14px;
}

.music-placeholder i {
  font-size: 20px;
}

.music-upload-wrapper {
  width: 100%;
}

.music-upload-wrapper >>> .el-upload {
  width: 100%;
  display: block;
}

.music-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.music-filename:hover {
  background: rgba(0, 0, 0, 0.7);
  border-color: #6366f1;
}

.music-filename:hover .label,
.music-filename:hover .filename {
  color: white;
}

.music-filename:hover .music-overlay {
  opacity: 1;
}

.music-filename .label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  transition: color 0.3s ease;
}

.music-filename .filename {
  font-size: 12px;
  color: #374151;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
}

.music-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
}

.music-overlay i {
  font-size: 16px;
}

/* 固定节点操作按钮 */
.fixed-node-actions {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  justify-content: center;
}

/* AI节点布局 */
.ai-node-layout {
  display: flex;
  gap: 24px;
  padding: 24px 24px 0 24px;
  align-items: stretch;
}

.ai-node-left {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.ai-node-left .el-form {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.ai-node-right {
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-shrink: 0;
}

/* AI节点设置区域 */
.ai-node-settings {
  padding: 0 24px 24px 24px;
}

.ai-node-settings .el-form {
  margin-top: 16px;
}

/* AI节点操作按钮 */
.ai-node-actions {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  justify-content: center;
}

/* 当前剧情设置样式 */
.current-plot-item {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.current-plot-item >>> .el-form-item__content {
  flex: 1;
  display: flex;
}

.current-plot-container {
  flex: 1;
  border: 1px solid #e5e7eb;
  padding: 15px;
  border-radius: 8px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.plot-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.plot-row:last-child {
  margin-bottom: 0;
}

.plot-label {
  display: inline-block;
  width: 80px;
  font-size: 13px;
  color: #606266;
  line-height: 32px;
  flex-shrink: 0;
}

.plot-textarea {
  flex: 1;
}

.plot-textarea >>> .el-textarea__inner {
  resize: vertical;
  min-height: auto !important;
}

/* 表单优化 */
.el-form-item {
  margin-bottom: 18px;
}

.el-form-item__label {
  font-weight: 500;
  color: #374151;
}

/* 上传按钮 */
.upload-bg, .upload-music, .upload-cover {
  display: inline-block;
}

.drag-dialogue-handle {
  user-select: none;
}

/* 分隔线样式 */
.el-divider {
  margin: 28px 0;
  background-color: #e5e7eb;
}

.el-divider__text {
  font-weight: 600;
  color: #6366f1;
  background-color: #ffffff;
  padding: 0 16px;
}

/* 条件/奖励/配置块样式优化 */
[style*="border: 1px solid #E6A23C"],
[style*="border: 1px solid #409EFF"],
[style*="border: 1px solid #67C23A"] {
  border-radius: 8px !important;
  transition: all 0.2s;
}

[style*="border: 1px solid #E6A23C"]:hover {
  box-shadow: 0 2px 8px rgba(230, 162, 60, 0.15);
}

[style*="border: 1px solid #409EFF"]:hover {
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

[style*="border: 1px solid #67C23A"]:hover {
  box-shadow: 0 2px 8px rgba(103, 194, 58, 0.15);
}

.expand-result-section {
  margin-bottom: 16px;
}

.expand-result-label {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
  margin-bottom: 4px;
}

.expand-result-content {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f5f7fa;
  padding: 10px 12px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
  color: #606266;
  font-family: inherit;
}

.management-bar {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin: 20px 0;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}
</style>





